/*
 * BSE — 3D-Balkonkraftwerk-Konfigurator
 * Web-Component <bkw-konfigurator> für Shopify-Themes.
 *
 * Eigenschaften:
 *  - vollständig container-relativ (kein 100vw/100vh, kein position:fixed)
 *  - Shadow DOM: Theme-CSS kann die Bedienelemente nicht überschreiben,
 *    Schriftart wird aus dem Theme geerbt
 *  - three.js wird per dynamischem import() geladen — KEINE importmap nötig
 *  - lädt erst, wenn die Section in Sichtweite kommt (IntersectionObserver)
 *  - sauberes dispose() beim Entfernen → verträgt shopify:section:load/unload
 *  - Touch: senkrecht scrollt die Seite, waagerecht dreht das Modell;
 *    kein Wheel-Zoom, damit die Seite beim Scrollen nicht hängen bleibt
 *
 * Attribute:
 *   three-src   URL zu three.module.js (default: unpkg CDN).
 *               Im Theme besser als eigenes Asset hinterlegen.
 *   autorotate  Drehteller bis zur ersten Interaktion (default an)
 *
 * Maße der Bauteile sind plausible Näherungen — die mit TODO markierten
 * Werte gegen die echten Herstellerdatenblätter tauschen.
 */
(function () {
  'use strict';
  if (customElements.get('bkw-konfigurator')) return;

  const DEFAULT_THREE = 'https://unpkg.com/three@0.184.0/build/three.module.js';
  const threeCache = new Map();
  const loadThree = (url) => {
    if (!threeCache.has(url)) threeCache.set(url, import(/* webpackIgnore: true */ url));
    return threeCache.get(url);
  };

  /* ---------------------------------------------------------------- Sets */
  /* Die drei realen Komplettsets des Shops. Bei den Premium-Sets sitzt der
     Wechselrichter im Speicher — deshalb kein separates Gehäuse an der Wand. */
  const SETS = [
    {
      id: 'smart-455',
      name: '455 Wp Smart',
      sub: 'ohne Speicher',
      price: '€399,80',
      url: '/products/tepto-455-wp-balkonkraftwerk-smart-hoymiles',
      // TODO: Trina NEG9R.28 — Maße aus Datenblatt bestätigen
      panel: { label: 'Trina NEG9RC.27', spec: '455 Wp', w: 1.762, h: 1.134, t: 0.030,
               style: 'trina', cols: 6, rowsPerHalf: 11, frame: 'frameBlack', cell: 'cellTrina' },
      inverter: { kind: 'box', label: 'Hoymiles HMS-800W-2T', spec: '800 W' },
      battery: null
    },
    {
      id: 'avocado',
      name: '500 Wp Premium',
      sub: 'Avocado 22 Pro · 2,11 kWh',
      price: '€932,80',
      url: '/products/tepto-500-w-balkonkraftwerk-premium-avocado-22-pro-2-11-kwh',
      // HD108-Black, Full-Black bifazial — TODO: Maße aus Datenblatt bestätigen
      panel: { label: 'HD108-Black', spec: '500 Wp', w: 1.90, h: 1.134, t: 0.030,
               style: 'trina', cols: 6, rowsPerHalf: 9, frame: 'frameBlack',
               cell: 'cellBlack', gridWidth: 0.0026, grid: 'gridDark', brand: 'HD108-Black', brandColor: '#1b1d1f' },
      inverter: { kind: 'integriert', label: 'Im Speicher integriert', spec: '800 W' },
      battery: { kind: 'avocado', label: 'Avocado 22 Pro', spec: '2,11 kWh' }
    },
    {
      id: 'sigenmate',
      name: '500 Wp Premium',
      sub: 'SigenMate · 2,69 kWh',
      price: '€1.712,80',
      url: '/products/tepto-500-w-balkonkraftwerk-premium-sigenmate-2-7-kwh',
      panel: { label: 'Bifazial Glas-Glas', spec: '500 Wp', w: 1.961, h: 1.134, t: 0.030,
               cols: 11, rows: 6, glass: 'glassBlue', cell: 'cellBlue', frame: 'frameSilver', busbars: 3 },
      inverter: { kind: 'integriert', label: 'Im Speicher integriert', spec: 'bis 4.000 W PV' },
      battery: { kind: 'stapel', label: 'SigenMate', spec: '2,69 kWh' }
    }
  ];

  const INFO = {
    panelDach: 'Auf dem Flachdach stehen die Module schräg nach Süden — hier mit 30° Neigung. So fangen sie deutlich mehr Sonne ein als senkrecht am Geländer.',
    mount: 'Je Modul zwei Aufständerungsdreiecke 20–45° (Aluminium, silber) und zwei Aluminium-Montageschienen à 1,2 m. Die Neigung lässt sich zwischen 20° und 45° einstellen.',
    panelTrapez: 'Auf dem Trapezblechdach liegen die Module flach und parallel zur Dachfläche — unauffällig, windgünstig und ohne die Dachhaut großflächig zu durchdringen.',
    mountTrapez: 'Je Modul vier Trapezprofil-Schienen (400 mm) auf den Hochsicken, darauf quer zwei schwarze Alu-Montageschienen à 1,2 m, verbunden über vier Flachverbinder (4-Loch) mit Hammerkopfschrauben M10 und Sperrzahnmuttern (V2A). Vier Klick-Endklemmen (30 mm) halten das Modul, Rechteckkappen schließen die Schienenenden.',
    panelZaun: 'Am Zaun hängen die Module senkrecht im Querformat — ideal an einer sonnigen Gartengrenze nach Süden, ganz ohne Bohren in die Hauswand.',
    mountZaun: 'Je Modul zwei schwarze Aluminium-Montageschienen à 1,2 m, senkrecht vor die Zaunmatte gesetzt. Vier Flachverbinder (4-Loch) klemmen die Schienen von hinten an die Querdrähte, vier Klick-Endklemmen (30 mm) halten das Modul, Rechteckkappen 40×40×28 mm schließen die Schienenenden.',
    panel: 'Die Photovoltaikmodule nutzen die Strahlung der Sonne, um Gleichstrom (DC) zu erzeugen. Am Balkongeländer stehen sie senkrecht — die Montageart aus dem Set.',
    inverterBox: 'Der Wechselrichter wandelt den Gleichstrom (DC) der Module in Wechselstrom (AC) um, damit ihn Haushaltsgeräte nutzen können. Der Netz- und Anlagenschutz nach VDE-AR-N 4105 ist integriert.',
    inverterInt: 'Bei diesem Set sitzt der Wechselrichter im Speicher. Er wandelt den Gleichstrom (DC) der Module in Wechselstrom (AC) um — ein separates Gehäuse an der Wand entfällt.',
    battery: 'Die Speichereinheit nimmt den Mittagsüberschuss auf und gibt ihn abends wieder ab. Der Eigenverbrauch steigt dadurch von rund 60–70 % auf bis zu 90 %.',
    socket: 'Über die Schuko-Steckdose gelangt der Wechselstrom (AC) direkt in das Stromnetz der Wohnung und versorgt zuerst die Geräte, die gerade laufen.'
  };

  const CSS = `
    :host { display: block; position: relative; width: 100%; container-type: size; container-name: bkw;
            height: var(--bkw-height, 80vh); min-height: 420px;
            background: var(--bkw-bg, #eceae5); overflow: hidden;
            font-family: inherit; color: #22262a;
            -webkit-font-smoothing: antialiased; }
    .wrap { position: absolute; inset: 0; }
    canvas { position: absolute; inset: 0; width: 100%; height: 100%; display: block;
             touch-action: pan-y; cursor: grab; }
    canvas:active { cursor: grabbing; }

    .hint { position: absolute; right: 14px; bottom: 12px; z-index: 4;
            font-size: 11.5px; letter-spacing: 0.04em; text-transform: uppercase;
            color: rgba(34,38,42,0.42); pointer-events: none; }

    .layer { position: absolute; inset: 0; pointer-events: none; z-index: 3; }
    .hs { position: absolute; transform: translate(-50%, -50%); pointer-events: auto; z-index: 1;
          transition: opacity 120ms ease; }
    .hs:hover, .hs:focus-within { z-index: 10; }
    .layer:has(.hs:hover) .hs:not(:hover) .dot,
    .layer:has(.hs:focus-within) .hs:not(:focus-within) .dot { opacity: 0.3; }
    .dot { width: 34px; height: 34px; border-radius: 50%; border: 1px solid rgba(30,34,38,0.35);
           background: rgba(255,255,255,0.42); backdrop-filter: blur(3px);
           box-shadow: 0 2px 10px rgba(20,24,28,0.18), inset 0 1px 0 rgba(255,255,255,0.75);
           color: #1e2226; font-size: 17px; font-weight: 600; line-height: 32px; text-align: center;
           cursor: help; transition: background 140ms ease, transform 140ms ease, opacity 140ms ease; }
    .dot:focus-visible { outline: 2px solid #22262a; outline-offset: 2px; }
    .hs:hover .dot { background: rgba(255,255,255,0.78); transform: scale(1.08); }
    .tip { position: absolute; left: 50%; bottom: 46px; width: 268px; padding: 14px 16px;
           transform: translateX(-50%); background: rgba(252,251,249,0.96);
           border: 1px solid rgba(30,34,38,0.12); border-radius: 4px;
           box-shadow: 0 10px 30px rgba(20,24,28,0.16); color: #22262a;
           font-size: 13.5px; line-height: 1.5; text-wrap: pretty;
           opacity: 0; visibility: hidden; transition: opacity 140ms ease; }
    .tip b { display: block; margin-bottom: 4px; font-size: 11.5px; font-weight: 600;
             letter-spacing: 0.06em; text-transform: uppercase; color: #8b8f93; }
    .hs:hover .tip, .hs:focus-within .tip { opacity: 1; visibility: visible; }
    .hs.down .tip { bottom: auto; top: 46px; }
    .hs.fl .tip { transform: translateX(-88%); }
    .hs.fr .tip { transform: translateX(-12%); }

    .card { position: absolute; top: 22px; left: 22px; z-index: 6; width: 272px;
            background: rgba(252,251,249,0.94); border: 1px solid rgba(30,34,38,0.12);
            border-radius: 5px; box-shadow: 0 12px 34px rgba(20,24,28,0.14);
            padding: 15px 15px 13px; backdrop-filter: blur(4px); box-sizing: border-box;
            max-height: calc(100% - 44px); overflow-y: auto; }
    .card h2 { margin: 0 0 3px; font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase;
               font-weight: 600; color: #6d7176; font-family: inherit; }
    .card p.lead { margin: 0 0 14px; font-size: 12.5px; line-height: 1.45; color: #6d7176; }
    .grp { margin-bottom: 13px; }
    .lbl { font-size: 11.5px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase;
           color: #8b8f93; margin-bottom: 6px; }
    .opts { display: flex; flex-direction: column; gap: 4px; }
    .opt { display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
           width: 100%; text-align: left; padding: 7px 10px; font: inherit; font-size: 13px;
           line-height: 1.3; color: #22262a; background: rgba(255,255,255,0.5);
           border: 1px solid rgba(30,34,38,0.14); border-radius: 3px; cursor: pointer;
           transition: background 120ms ease, border-color 120ms ease; }
    .opt:hover { background: #fff; border-color: rgba(30,34,38,0.3); }
    .opt .spec { font-size: 11.5px; color: #8b8f93; white-space: nowrap; }
    .opt.stack { flex-direction: column; align-items: flex-start; gap: 1px; }
    .opt.stack .spec { white-space: normal; }
    .opt[aria-pressed="true"] { background: #22262a; border-color: #22262a; color: #f6f5f2; }
    .opt[aria-pressed="true"] .spec { color: rgba(246,245,242,0.7); }
    .row { display: flex; gap: 4px; }
    .row .opt { justify-content: center; }
    .parts { display: flex; flex-direction: column; gap: 3px; margin: 0 0 13px;
             font-size: 12px; line-height: 1.45; color: #6d7176; }
    .parts div { display: flex; justify-content: space-between; gap: 10px; }
    .parts span { color: #22262a; text-align: right; }
    .cta { display: flex; align-items: center; justify-content: space-between; gap: 10px;
           margin-top: 2px; padding: 10px 12px; border-radius: 3px; background: #22262a;
           color: #f6f5f2; font-size: 13px; font-weight: 600; text-decoration: none;
           transition: background 120ms ease; }
    .cta:hover { background: #32373c; color: #f6f5f2; }
    .cta .price { font-weight: 400; color: rgba(246,245,242,0.78); }

    .fallback { position: absolute; inset: 0; display: grid; place-items: center; padding: 24px;
                text-align: center; font-size: 14px; line-height: 1.5; color: #6d7176; }

    @media (max-width: 749px) {
      :host { height: var(--bkw-height-mobile, 78vh); min-height: 460px; }
      .card { top: 12px; left: 12px; width: 214px; padding: 12px 12px 11px; }
      .card p.lead { display: none; }
      .opt { padding: 6px 8px; font-size: 12px; }
      .opt .spec { font-size: 10.5px; }
      .parts { font-size: 11px; }
      .tip { width: 214px; font-size: 12.5px; padding: 11px 13px; }
      .hint { font-size: 10px; }
    }
    @container bkw (max-height: 760px) {
      .card p.lead { display: none; }
      .card h2 { margin-bottom: 10px; }
      .grp { margin-bottom: 10px; }
      .opt { padding: 6px 10px; }
    }
    @container bkw (max-height: 580px) {
      .card { top: 14px; left: 14px; padding: 12px 12px 11px; }
      .card p.lead, .parts { display: none; }
      .card h2 { margin-bottom: 9px; }
      .grp { margin-bottom: 10px; }
      .opt { padding: 6px 9px; }
    }
    @media (prefers-reduced-motion: reduce) { .hs, .dot, .opt, .cta { transition: none; } }
  `;

  /* --------------------------------------------------- Mini-Orbit-Steuerung
     Ersetzt OrbitControls: kein bare import, kein Wheel-Zoom (die Seite soll
     scrollbar bleiben), Drehung um das Modellzentrum mit Dämpfung. */
  function makeOrbit(THREE, camera, dom, target) {
    const sph = new THREE.Spherical();
    const off = new THREE.Vector3();
    const st = { theta: 0, phi: 1.1, radius: 6, dTheta: 0, dPhi: 0,
                 autoRotate: false, speed: 0.32, damping: 0.09, used: false };

    const sync = () => {
      off.copy(camera.position).sub(target);
      sph.setFromVector3(off);
      st.theta = sph.theta; st.phi = sph.phi; st.radius = sph.radius;
    };
    const apply = () => {
      sph.set(st.radius, THREE.MathUtils.clamp(st.phi, 0.72, 1.46), st.theta);
      off.setFromSpherical(sph);
      camera.position.copy(target).add(off);
      camera.lookAt(target);
    };

    let drag = null;
    const down = (e) => {
      if (st.goal) st.goal.phi = null;
      if (e.pointerType === 'touch') { drag = { id: e.pointerId, x: e.clientX, y: e.clientY, locked: null }; return; }
      dom.setPointerCapture(e.pointerId);
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, locked: 'x' };
      st.used = true; st.autoRotate = false;
    };
    const move = (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (drag.locked === null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        // senkrechte Geste gehört der Seite, waagerechte dem Modell
        if (Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
        drag.locked = 'x';
        st.used = true; st.autoRotate = false;
        try { dom.setPointerCapture(e.pointerId); } catch (_) {}
      }
      drag.x = e.clientX; drag.y = e.clientY;
      st.dTheta -= dx * 0.0055;
      if (e.pointerType !== 'touch') st.dPhi -= dy * 0.004;
      e.preventDefault();
    };
    const up = (e) => {
      if (drag && e.pointerId === drag.id) {
        try { dom.releasePointerCapture(e.pointerId); } catch (_) {}
        drag = null;
      }
    };
    dom.addEventListener('pointerdown', down);
    dom.addEventListener('pointermove', move);
    dom.addEventListener('pointerup', up);
    dom.addEventListener('pointercancel', up);

    return {
      target, state: st,
      sync, apply,
      setDistance(d) { st.radius = d; },
      glide(t, r, phi) { st.goal = { t: t.clone(), r, phi }; },
      update(dt) {
        if (st.autoRotate) st.theta -= st.speed * dt;
        if (st.goal) {
          const k = 1 - Math.exp(-dt * 3.2), gl = st.goal;
          target.lerp(gl.t, k);
          st.radius += (gl.r - st.radius) * k;
          if (gl.phi != null) st.phi += (gl.phi - st.phi) * k;
          if (target.distanceTo(gl.t) < 1e-3 && Math.abs(gl.r - st.radius) < 1e-3 &&
              (gl.phi == null || Math.abs(gl.phi - st.phi) < 1e-3)) st.goal = null;
        }
        st.theta += st.dTheta; st.phi += st.dPhi;
        st.dTheta *= 1 - st.damping; st.dPhi *= 1 - st.damping;
        if (Math.abs(st.dTheta) < 1e-5) st.dTheta = 0;
        if (Math.abs(st.dPhi) < 1e-5) st.dPhi = 0;
        apply();
      },
      dispose() {
        dom.removeEventListener('pointerdown', down);
        dom.removeEventListener('pointermove', move);
        dom.removeEventListener('pointerup', up);
        dom.removeEventListener('pointercancel', up);
      }
    };
  }

  /* ------------------------------------------------------------- Component */
  class BkwKonfigurator extends HTMLElement {
    constructor() {
      super();
      this._booted = false;
      this._dead = false;
      this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
      if (this._built) return;
      this._built = true;
      this._dead = false;
      const sr = this.shadowRoot;
      const style = document.createElement('style');
      style.textContent = CSS;
      sr.appendChild(style);

      this._wrap = document.createElement('div');
      this._wrap.className = 'wrap';
      this._wrap.innerHTML =
        '<div class="layer"></div>' +
        '<div class="card"></div>' +
        '<div class="hint">Ziehen zum Drehen</div>';
      sr.appendChild(this._wrap);

      this._layer = this._wrap.querySelector('.layer');
      this._card = this._wrap.querySelector('.card');
      this._buildCard();

      // erst laden, wenn die Section in die Nähe des Viewports kommt
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this._io.disconnect(); this._io = null;
            this._boot();
          }
        }, { rootMargin: '300px 0px' });
        this._io.observe(this);
      } else {
        this._boot();
      }
    }

    disconnectedCallback() { this._dispose(); }

    _dispose() {
      this._dead = true;
      if (this._io) { this._io.disconnect(); this._io = null; }
      if (this._ro) { this._ro.disconnect(); this._ro = null; }
      if (this._vis) { this._vis.disconnect(); this._vis = null; }
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = 0;
      if (this._orbit) { this._orbit.dispose(); this._orbit = null; }
      if (this._scene) {
        this._scene.traverse((o) => {
          if (o.isMesh) {
            o.geometry && o.geometry.dispose();
            const m = o.material;
            if (Array.isArray(m)) m.forEach((x) => x.dispose()); else m && m.dispose();
          }
        });
        this._scene = null;
      }
      if (this._renderer) {
        this._renderer.dispose();
        const c = this._renderer.domElement;
        c && c.parentNode && c.parentNode.removeChild(c);
        this._renderer = null;
      }
      this._built = false;
      if (this.shadowRoot) this.shadowRoot.innerHTML = '';
    }

    /* ------------------------------------------------------- Konfigurator-UI */
    _buildCard() {
      this._cfg = { mount: 'balkon', set: 0, modules: 1 };
      const c = this._card;
      c.innerHTML =
        '<h2>Anlage ansehen</h2>' +
        '<p class="lead">Montageart und Set wählen — das Modell baut sich neu auf und bleibt drehbar.</p>' +
        '<div class="grp"><div class="lbl">Montageart</div><div class="row" data-k="mount" style="display:grid;grid-template-columns:1fr 1fr"></div></div>' +
        '<div class="grp"><div class="lbl">Komplettset</div><div class="opts" data-k="set"></div></div>' +
        '<div class="grp"><div class="lbl">Anzahl Module</div><div class="row" data-k="modules"></div></div>' +
        '<div class="parts"></div>' +
        '<a class="cta" href="#"><span>Zum Set</span><span class="price"></span></a>';

      const mountBox = c.querySelector('[data-k="mount"]');
      [['balkon', 'Balkon'], ['dach', 'Flachdach'], ['zaun', 'Zaun'], ['trapez', 'Trapezdach']].forEach(([k, t]) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'opt';
        b.dataset.v = k;
        b.textContent = t;
        b.addEventListener('click', () => this._select('mount', k));
        mountBox.appendChild(b);
      });

      const setBox = c.querySelector('[data-k="set"]');
      SETS.forEach((s, i) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'opt stack';
        b.setAttribute('aria-pressed', String(i === 0));
        b.innerHTML = '<span></span><span class="spec"></span>';
        b.children[0].textContent = s.name;
        b.children[1].textContent = s.sub;
        b.addEventListener('click', () => this._select('set', i));
        setBox.appendChild(b);
      });

      const modBox = c.querySelector('[data-k="modules"]');
      [1, 2].forEach((n, i) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'opt';
        b.setAttribute('aria-pressed', String(i === 0));
        b.textContent = n === 1 ? '1 Modul' : '2 Module';
        b.addEventListener('click', () => this._select('modules', n));
        modBox.appendChild(b);
      });

      this._parts = c.querySelector('.parts');
      this._cta = c.querySelector('.cta');
      this._syncCard();
    }

    _select(key, value) {
      if (this._cfg[key] === value) return;
      this._cfg[key] = value;
      this._syncCard();
      if (this._rebuild) this._rebuild();
    }

    _syncCard() {
      const c = this._card, cfg = this._cfg, s = SETS[cfg.set];
      c.querySelectorAll('[data-k="mount"] .opt').forEach((b) =>
        b.setAttribute('aria-pressed', String(b.dataset.v === cfg.mount)));
      c.querySelectorAll('[data-k="set"] .opt').forEach((b, i) =>
        b.setAttribute('aria-pressed', String(i === cfg.set)));
      c.querySelectorAll('[data-k="modules"] .opt').forEach((b, i) =>
        b.setAttribute('aria-pressed', String(i + 1 === cfg.modules)));

      const n = cfg.modules;
      const rows = cfg.mount === 'trapez' ? [
        ['Modul', s.panel.label + ' · ' + s.panel.spec],
        ['Trapezschienen', (4 * n) + '× Trapezprofil 400 mm'],
        ['Schienen', (2 * n) + '× Alu schwarz 1,2 m'],
        ['Endklemmen', (4 * n) + '× Klick ALU schwarz 30 mm'],
        ['Verbinder', (4 * n) + '× Flachverbinder 4-Loch'],
        ['Kappen', (4 * n) + '× Rechteck 40×40×28 mm'],
        ['Schrauben', (10 * n) + '× Hammerkopf M10×25 + Sperrzahnmutter, V2A']
      ] : cfg.mount === 'zaun' ? [
        ['Modul', s.panel.label + ' · ' + s.panel.spec],
        ['Schienen', (2 * n) + '× Alu schwarz 1,2 m'],
        ['Endklemmen', (4 * n) + '× Klick ALU schwarz 30 mm'],
        ['Verbinder', (4 * n) + '× Flachverbinder 4-Loch'],
        ['Kappen', (4 * n) + '× Rechteck 40×40×28 mm']
      ] : cfg.mount === 'dach' ? [
        ['Modul', s.panel.label + ' · ' + s.panel.spec],
        ['Montage', 'je Modul 2 Dreiecke + 2 Schienen']
      ] : [
        ['Modul', s.panel.label + ' · ' + s.panel.spec],
        ['Wechselrichter', s.inverter.label],
        ['Speicher', s.battery ? s.battery.label + ' · ' + s.battery.spec : 'ohne'],
        ['Montage', 'Geländerhalterung']
      ];
      this._parts.innerHTML = rows
        .map((r) => '<div><em style="font-style:normal">' + r[0] + '</em><span></span></div>')
        .join('');
      this._parts.querySelectorAll('span').forEach((el, i) => { el.textContent = rows[i][1]; });

      this._cta.href = s.url;
      this._cta.querySelector('.price').textContent = s.price;
      this._cta.setAttribute('aria-label', 'Zum Set ' + s.name + ' ' + s.sub + ', ' + s.price);
      this._applyVariant();
    }

    /* Shopify-Variante zur Montageart vorwählen (?variant=…), Preis der Variante übernehmen.
       Läuft nur im Shop (gleiche Domain); sonst bleibt der Link auf das Produkt. */
    _applyVariant() {
      const cfg = this._cfg, s = SETS[cfg.set];
      const re = { balkon: /balkon/i, dach: /flachdach/i, zaun: /zaun/i, trapez: /trapez/i }[cfg.mount];
      this._variants = this._variants || {};
      if (!this._variants[s.url]) {
        this._variants[s.url] = fetch(s.url + '.js', { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null)).then((p) => (p && p.variants) || []).catch(() => []);
      }
      this._variants[s.url].then((vs) => {
        if (SETS[this._cfg.set] !== s || !re.test(this._cfg.mount === 'dach' ? 'flachdach' : this._cfg.mount)) return;
        const v = vs.find((x) => re.test([x.title, x.option1, x.option2, x.option3].join(' ')));
        if (!v) return;
        const price = '€' + (v.price / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        this._cta.href = s.url + '?variant=' + v.id;
        this._cta.querySelector('.price').textContent = price;
        this._cta.setAttribute('aria-label', 'Zum Set ' + s.name + ' ' + s.sub + ' mit ' + v.title + ', ' + price);
      });
    }

    /* ------------------------------------------------------------ 3D-Aufbau */
    async _boot() {
      if (this._booted) return;
      this._booted = true;
      let THREE;
      try {
        THREE = await loadThree(this.getAttribute('three-src') || DEFAULT_THREE);
      } catch (err) {
        const f = document.createElement('div');
        f.className = 'fallback';
        f.textContent = 'Die 3D-Ansicht konnte nicht geladen werden. Die Sets findest du unter „Komplettpakete“.';
        this._wrap.appendChild(f);
        return;
      }
      if (this._dead || !this.isConnected) return;

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: 'high-performance' });
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2));
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFShadowMap;
      this._renderer = renderer;
      this._wrap.insertBefore(renderer.domElement, this._layer);

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(getComputedStyle(this).getPropertyValue('--bkw-bg').trim() || '#eceae5');
      this._scene = scene;
      const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 120);
      camera.position.set(5, 3, 6);
      this._camera = camera;

      const hemi = new THREE.HemisphereLight(0xffffff, 0xd8d2c4, 1.0);
      scene.add(hemi);
      const key = new THREE.DirectionalLight(0xffffff, 2.2);
      key.position.set(4.2, 6.4, 4.6);
      key.castShadow = true;
      key.shadow.mapSize.set(mobile ? 2048 : 4096, mobile ? 2048 : 4096);
      key.shadow.bias = -0.00035;
      key.shadow.normalBias = 0.012;
      scene.add(key); scene.add(key.target);
      const fill = new THREE.DirectionalLight(0xfff4e6, 0.5);
      fill.position.set(-5, 3, -4);
      scene.add(fill);

      const groundMat = new THREE.ShadowMaterial({ opacity: 0.18 });
      const ground = new THREE.Mesh(new THREE.PlaneGeometry(200, 200), groundMat);
      ground.rotation.x = -Math.PI / 2;
      ground.receiveShadow = true;
      scene.add(ground);

      const M = {
        panelGlass:   new THREE.MeshStandardMaterial({ color: 0x14161a, roughness: 0.28, metalness: 0.3 }),
        panelCell:    new THREE.MeshStandardMaterial({ color: 0x1d2128, roughness: 0.42, metalness: 0.25 }),
        panelFrame:   new THREE.MeshStandardMaterial({ color: 0x2a2c30, roughness: 0.5,  metalness: 0.35 }),
        glassBlue:    new THREE.MeshStandardMaterial({ color: 0x1b2430, roughness: 0.22, metalness: 0.35 }),
        cellBlue:     new THREE.MeshStandardMaterial({ color: 0x2c3a4c, roughness: 0.38, metalness: 0.3 }),
        frameSilver:  new THREE.MeshStandardMaterial({ color: 0xa9adb1, roughness: 0.3,  metalness: 0.45 }),
        busbar:       new THREE.MeshStandardMaterial({ color: 0xd2d5d8, roughness: 0.25, metalness: 0.5 }),
        frameBlack:   new THREE.MeshStandardMaterial({ color: 0x191a1d, roughness: 0.44, metalness: 0.42 }),
        glassDark:    new THREE.MeshStandardMaterial({ color: 0x0f1113, roughness: 0.16, metalness: 0.28 }),
        cellTrina:    new THREE.MeshStandardMaterial({ color: 0x191b20, roughness: 0.36, metalness: 0.22 }),
        gridWhite:    new THREE.MeshStandardMaterial({ color: 0xe7e9eb, roughness: 0.34, metalness: 0.18 }),
        cellBlack:    new THREE.MeshStandardMaterial({ color: 0x101114, roughness: 0.32, metalness: 0.2 }),
        avoSilver:    new THREE.MeshStandardMaterial({ color: 0x8d9195, roughness: 0.52, metalness: 0.5 }),
        avoDark:      new THREE.MeshStandardMaterial({ color: 0x1b1d1f, roughness: 0.5,  metalness: 0.35 }),
        avoRib:       new THREE.MeshStandardMaterial({ color: 0x141618, roughness: 0.44, metalness: 0.4 }),
        housing:      new THREE.MeshStandardMaterial({ color: 0x232529, roughness: 0.62, metalness: 0.3 }),
        housingLight: new THREE.MeshStandardMaterial({ color: 0x9ea2a6, roughness: 0.45, metalness: 0.35 }),
        batteryBody:  new THREE.MeshStandardMaterial({ color: 0x33373c, roughness: 0.55, metalness: 0.25 }),
        batteryPale:  new THREE.MeshStandardMaterial({ color: 0xdedbd5, roughness: 0.5,  metalness: 0.15 }),
        batteryTrim:  new THREE.MeshStandardMaterial({ color: 0xb9bcc0, roughness: 0.32, metalness: 0.4 }),
        steel:        new THREE.MeshStandardMaterial({ color: 0xc3c6c9, roughness: 0.3,  metalness: 0.4 }),
        cable:        new THREE.MeshStandardMaterial({ color: 0x121316, roughness: 0.75, metalness: 0.05 }),
        concrete:     new THREE.MeshStandardMaterial({ color: 0xd8d4cc, roughness: 0.9,  metalness: 0.0 }),
        wall:         new THREE.MeshStandardMaterial({ color: 0xe6e3dd, roughness: 0.95, metalness: 0.0 }),
        gridDark:     new THREE.MeshStandardMaterial({ color: 0x1e2024, roughness: 0.35, metalness: 0.5 }),
        doorGlass:    new THREE.MeshStandardMaterial({ color: 0x2a3138, roughness: 0.15, metalness: 0.3 }),
        doorFrame:    new THREE.MeshStandardMaterial({ color: 0x3a3d41, roughness: 0.5,  metalness: 0.35 }),
        infill:       new THREE.MeshStandardMaterial({ color: 0xdfe6ea, roughness: 0.4, transparent: true, opacity: 0.3, depthWrite: false }),
        led:          new THREE.MeshStandardMaterial({ color: 0x7bd88f, roughness: 0.3, metalness: 0.0,
                                                       emissive: 0x3f9c55, emissiveIntensity: 0.6 })
      };

      /* ---------------------------------------------------- Canvas-Texturen */
      const canvasTex = (w, h, draw, repeat) => {
        const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
        draw(cv.getContext('2d'), w, h);
        const t = new THREE.CanvasTexture(cv);
        t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = renderer.capabilities.getMaxAnisotropy ? Math.min(8, renderer.capabilities.getMaxAnisotropy()) : 4;
        if (repeat) { t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(repeat[0], repeat[1]); }
        return t;
      };
      const noise = (x, w, h, a, b, n) => {
        for (let i = 0; i < n; i++) {
          const v = a + Math.random() * (b - a);
          x.fillStyle = 'rgba(' + v + ',' + v + ',' + v + ',' + (0.04 + Math.random() * 0.06) + ')';
          x.fillRect(Math.random() * w, Math.random() * h, 1 + Math.random() * 2, 1 + Math.random() * 2);
        }
      };
      // Halbzelle: Busbars senkrecht, feine Kontaktfinger waagerecht
      const cellTex = (base, finger, bus, buses) => canvasTex(256, 256, (x, w, h) => {
        x.fillStyle = base; x.fillRect(0, 0, w, h);
        x.fillStyle = finger;
        for (let y = 2; y < h; y += 5) x.fillRect(0, y, w, 1);
        x.fillStyle = bus;
        for (let i = 0; i < buses; i++) x.fillRect(Math.round((i + 0.5) * w / buses) - 1, 0, 2, h);
        // Fasen an den Zellecken
        x.fillStyle = 'rgba(0,0,0,0.55)';
        [[0, 0], [w, 0], [0, h], [w, h]].forEach(([cx, cy]) => {
          x.beginPath(); x.moveTo(cx, cy); x.lineTo(cx + (cx ? -14 : 14), cy); x.lineTo(cx, cy + (cy ? -14 : 14)); x.fill();
        });
      });
      const labelTex = (text, o) => canvasTex(o.w || 512, o.h || 128, (x, w, h) => {
        if (o.bg) { x.fillStyle = o.bg; x.fillRect(0, 0, w, h); }
        x.fillStyle = o.color || '#ffffff';
        x.font = (o.weight || 700) + ' ' + (o.size || h * 0.55) + 'px Helvetica, Arial, sans-serif';
        x.textAlign = 'center'; x.textBaseline = 'middle';
        if ('letterSpacing' in x && o.spacing) x.letterSpacing = o.spacing + 'px';
        x.fillText(text, w / 2, o.sub ? h * 0.36 : h / 2);
        if (o.sub) {
          x.fillStyle = o.subColor || x.fillStyle;
          x.font = '500 ' + Math.round(h * 0.16) + 'px Helvetica, Arial, sans-serif';
          o.sub.forEach((line, i) => x.fillText(line, w / 2, h * (0.62 + i * 0.17)));
        }
      });
      const TXI = {};   // Geometrie der gezeichneten Ziegelreihen, für passende UVs an Ober-/Seitenflächen
      const TX = {
        tiles: canvasTex(1024, 364, (x, w, h) => {
          x.fillStyle = '#d3cdc2'; x.fillRect(0, 0, w, h);
          noise(x, w, h, 150, 240, 9000);
          const step = w / 13.2;
          x.fillStyle = '#b3ac9f';
          for (let i = 1; i * step < w; i++) x.fillRect(Math.round(i * step), 0, 2, h);
          for (let j = 1; j * step < h; j++) x.fillRect(0, Math.round(j * step), w, 2);
        }),
        // Ziegelmauerwerk im Läuferverband (Vorlage: Kunden-Clipart) — flach, halber Versatz je Reihe
        bricks: canvasTex(2048, 622, (x, w, h) => {
          const pxPerM = w / 3.95;
          const bw = 0.24 * pxPerM, bh = 0.078 * pxPerM, j = 0.009 * pxPerM, r = bh * 0.1;
          let seed = 11;
          const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
          // Farbanteile wie in der Vorlage: überwiegend Ziegelrot, einzelne dunkle und helle Steine
          const tones = [
            ['#a8624f', 46], ['#b26e5b', 9], ['#985645', 11], ['#9a5a48', 8],
            ['#875043', 9], ['#7a493e', 7], ['#6a4239', 6], ['#bf8a79', 4]
          ];
          const total = tones.reduce((a, t) => a + t[1], 0);
          const pick = () => { let v = rnd() * total; for (const t of tones) { if ((v -= t[1]) < 0) return t[0]; } return tones[0][0]; };
          const rrect = (px, py, pw, ph) => {
            x.beginPath();
            x.moveTo(px + r, py); x.lineTo(px + pw - r, py); x.quadraticCurveTo(px + pw, py, px + pw, py + r);
            x.lineTo(px + pw, py + ph - r); x.quadraticCurveTo(px + pw, py + ph, px + pw - r, py + ph);
            x.lineTo(px + r, py + ph); x.quadraticCurveTo(px, py + ph, px, py + ph - r);
            x.lineTo(px, py + r); x.quadraticCurveTo(px, py, px + r, py);
            x.fill();
          };
          x.fillStyle = '#ddd1c4'; x.fillRect(0, 0, w, h);
          TXI.bricks = { w, h, rows: [] };
          for (let y = h - bh - j / 2, row = 0; y > -bh; y -= bh + j, row++) {
            TXI.bricks.rows.push([y, y + bh]);
            const off = row % 2 ? -(bw + j) / 2 : 0;
            for (let xx = off; xx < w; xx += bw + j) {
              x.fillStyle = pick();
              rrect(xx, y, bw, bh);
            }
          }
        }),
        brushed: (() => {
          const t = canvasTex(256, 256, (x, w, h) => {
            x.fillStyle = '#c8c8c8'; x.fillRect(0, 0, w, h);
            for (let y = 0; y < h; y++) {
              const v = 170 + Math.random() * 85;
              x.fillStyle = 'rgb(' + v + ',' + v + ',' + v + ')';
              x.fillRect(0, y, w, 1);
            }
          }, [6, 6]);
          t.colorSpace = THREE.NoColorSpace; return t;
        })()
      };

      /* ------------------------------------------- Look: Licht & Materialien
         look="studio"     neutrales Produktlicht, wie Shop-Fotos
         look="tageslicht" warme Sonne, blauer Himmel in den Reflexionen
         ohne Attribut     bisheriger Look */
      const look = this.getAttribute('look') || 'tageslicht';   // look="klassisch" → alter Look
      if (look === 'studio' || look === 'tageslicht') {
        const day = look === 'tageslicht';
        renderer.toneMapping = THREE.NeutralToneMapping !== undefined ? THREE.NeutralToneMapping : THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = day ? 1.0 : 1.08;

        // Umgebung für Reflexionen: Raum mit Softboxen (studio) bzw. Himmel + Sonne (tageslicht)
        const env = new THREE.Scene();
        const emit = (hex, k) => new THREE.MeshBasicMaterial({ color: new THREE.Color(hex).multiplyScalar(k), side: THREE.BackSide });
        const shell = new THREE.Mesh(new THREE.SphereGeometry(20, 48, 24), new THREE.MeshBasicMaterial({ side: THREE.BackSide, vertexColors: true }));
        const pos = shell.geometry.attributes.position, col = [];
        const top = new THREE.Color(day ? 0x9fc3ec : 0xf2f2f2).multiplyScalar(day ? 1.5 : 1.25);
        const hor = new THREE.Color(day ? 0xe8eef3 : 0xbdbab4).multiplyScalar(day ? 1.2 : 0.75);
        const bot = new THREE.Color(day ? 0x8a8478 : 0x55534f).multiplyScalar(0.55);
        const c = new THREE.Color();
        for (let i = 0; i < pos.count; i++) {
          const y = pos.getY(i) / 20;
          if (y >= 0) c.copy(hor).lerp(top, Math.pow(y, 0.6)); else c.copy(hor).lerp(bot, Math.min(1, -y * 3));
          col.push(c.r, c.g, c.b);
        }
        shell.geometry.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
        env.add(shell);
        const panel = (w, h, hex, k, x, y, z) => {
          const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), emit(hex, k));
          m.material.side = THREE.DoubleSide;
          m.position.set(x, y, z); m.lookAt(0, 0, 0); env.add(m);
        };
        if (day) {
          const sun = new THREE.Mesh(new THREE.SphereGeometry(0.9, 16, 8), new THREE.MeshBasicMaterial({ color: new THREE.Color(0xfff1d6).multiplyScalar(40) }));
          sun.position.set(8.4, 12.8, 9.2); env.add(sun);
        } else {
          panel(9, 4, 0xffffff, 7, 0, 14, 4);       // Deckenlicht
          panel(5, 7, 0xfff6ea, 5, 12, 5, 8);       // Hauptsoftbox rechts vorn
          panel(4, 6, 0xeef3ff, 2.2, -12, 4, -4);   // Aufheller links hinten
          panel(10, 1.2, 0xffffff, 3, 0, 3, -14);   // Streifenlicht hinten → Kantenglanz
        }
        const pm = new THREE.PMREMGenerator(renderer);
        scene.environment = pm.fromScene(env, 0.035).texture;
        pm.dispose();
        env.traverse((o) => { if (o.isMesh) { o.geometry.dispose(); o.material.dispose(); } });
        if ('environmentIntensity' in scene) scene.environmentIntensity = day ? 0.85 : 1.0;

        hemi.intensity = day ? 0.35 : 0.2;
        key.color.set(day ? 0xfff0da : 0xffffff);
        key.intensity = day ? 3.2 : 2.0;
        fill.intensity = day ? 0.15 : 0.35;
        key.shadow.radius = day ? 3 : 6;
        key.shadow.blurSamples = 16;
        groundMat.opacity = day ? 0.3 : 0.22;

        const P = (o) => new THREE.MeshPhysicalMaterial(o);
        const S = (o) => new THREE.MeshStandardMaterial(o);
        Object.assign(M, {
          // Modulglas: dünne Klarlackschicht über den Zellen → echte Spiegelungen
          glassDark:   P({ color: 0x0b0c0e, roughness: 0.32, metalness: 0.0, clearcoat: 1, clearcoatRoughness: 0.04, ior: 1.52, specularIntensity: 0.6 }),
          panelGlass:  P({ color: 0x0e1013, roughness: 0.32, metalness: 0.0, clearcoat: 1, clearcoatRoughness: 0.04, ior: 1.52 }),
          glassBlue:   P({ color: 0x131b25, roughness: 0.3,  metalness: 0.0, clearcoat: 1, clearcoatRoughness: 0.04, ior: 1.52 }),
          cellTrina:   S({ color: 0xffffff, map: cellTex('#1a1d25', '#2b2f38', '#868b93', 16), roughness: 0.42, metalness: 0.35 }),
          cellBlack:   S({ color: 0xffffff, map: cellTex('#0e0f12', '#16171b', '#24262a', 16), roughness: 0.4,  metalness: 0.3 }),
          cellBlue:    S({ color: 0x24334a, roughness: 0.4,  metalness: 0.35 }),
          panelCell:   S({ color: 0x1a1d24, roughness: 0.42, metalness: 0.35 }),
          gridWhite:   S({ color: 0xd9dcdf, roughness: 0.3,  metalness: 0.6 }),
          gridDark:    S({ color: 0x1e2024, roughness: 0.35, metalness: 0.5 }),
          busbar:      S({ color: 0xd2d5d8, roughness: 0.25, metalness: 0.9 }),
          // eloxiertes Alu: metallisch, leicht matt
          frameBlack:  S({ color: 0x1b1c1f, roughness: 0.38, metalness: 0.8 }),
          frameSilver: S({ color: 0xb9bdc1, roughness: 0.32, metalness: 0.95 }),
          panelFrame:  S({ color: 0x2a2c30, roughness: 0.38, metalness: 0.8 }),
          steel:       S({ color: 0xd4d7da, roughness: 0.22, metalness: 1.0 }),
          // Kunststoffe: nicht metallisch, Mikrorauheit
          housing:     P({ color: 0x1e2023, roughness: 0.55, metalness: 0.0, sheen: 0.15, sheenRoughness: 0.6 }),
          housingLight:S({ color: 0x9ea2a6, roughness: 0.4,  metalness: 0.6 }),
          avoSilver:   S({ color: 0xa3a7ab, roughness: 0.5, roughnessMap: TX.brushed, metalness: 0.85 }),
          avoDark:     P({ color: 0x191b1d, roughness: 0.5,  metalness: 0.1, clearcoat: 0.3, clearcoatRoughness: 0.4 }),
          avoRib:      S({ color: 0x1a1c1e, roughness: 0.4,  metalness: 0.75 }),
          batteryBody: P({ color: 0x31353a, roughness: 0.5,  metalness: 0.0, clearcoat: 0.25, clearcoatRoughness: 0.5 }),
          batteryPale: P({ color: 0xe2dfd9, roughness: 0.45, metalness: 0.0, clearcoat: 0.4, clearcoatRoughness: 0.3 }),
          batteryTrim: S({ color: 0xc4c7cb, roughness: 0.28, metalness: 0.9 }),
          frameBlackSteel: S({ color: 0x191a1d, roughness: 0.4, metalness: 0.7 }),
          cable:       P({ color: 0x111215, roughness: 0.62, metalness: 0.0, sheen: 0.3, sheenRoughness: 0.5 }),
          concrete:    S({ color: 0xffffff, map: TX.tiles, roughness: 0.88, metalness: 0.0 }),
          wall:        S({ color: 0xffffff, map: TX.bricks, roughness: 0.92, metalness: 0.0 }),
          doorGlass:   P({ color: 0x1f262d, roughness: 0.04, metalness: 0.0, clearcoat: 1, clearcoatRoughness: 0.02, ior: 1.5 }),
          doorFrame:   S({ color: 0x3a3d41, roughness: 0.45, metalness: 0.55 }),
          infill:      P({ color: 0xdfe6ea, roughness: 0.35, metalness: 0.0, transparent: true, opacity: 0.32, depthWrite: false }),
          led:         S({ color: 0x7bd88f, roughness: 0.3, metalness: 0.0, emissive: 0x4fd06d, emissiveIntensity: 1.4 })
        });
      }

      M.roofTop = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.95, metalness: 0,
        map: canvasTex(512, 512, (x, w, h) => { x.fillStyle = '#8d8e8e'; x.fillRect(0, 0, w, h); noise(x, w, h, 60, 210, 26000); }, [4, 6]) });
      M.pavement = new THREE.MeshStandardMaterial({ color: 0xcdc8bf, roughness: 0.95, metalness: 0 });
      M.alu = M.frameSilver;

      const geoCache = new Map();
      const roundedGeo = (w, h, d) => {
        const min = Math.min(w, h, d);
        const b = Math.min(0.006, min * 0.2);
        const k = w.toFixed(4) + '|' + h.toFixed(4) + '|' + d.toFixed(4);
        if (geoCache.has(k)) return geoCache.get(k);
        const iw = w - 2 * b, ih = h - 2 * b, rs = Math.min(b, iw / 2, ih / 2);
        const sh = new THREE.Shape();
        sh.moveTo(-iw / 2 + rs, -ih / 2);
        sh.lineTo(iw / 2 - rs, -ih / 2); sh.quadraticCurveTo(iw / 2, -ih / 2, iw / 2, -ih / 2 + rs);
        sh.lineTo(iw / 2, ih / 2 - rs);  sh.quadraticCurveTo(iw / 2, ih / 2, iw / 2 - rs, ih / 2);
        sh.lineTo(-iw / 2 + rs, ih / 2); sh.quadraticCurveTo(-iw / 2, ih / 2, -iw / 2, ih / 2 - rs);
        sh.lineTo(-iw / 2, -ih / 2 + rs); sh.quadraticCurveTo(-iw / 2, -ih / 2, -iw / 2 + rs, -ih / 2);
        const g = new THREE.ExtrudeGeometry(sh, { depth: d - 2 * b, bevelEnabled: true, bevelThickness: b,
          bevelSize: b, bevelSegments: 3, curveSegments: 3 });
        g.translate(0, 0, -(d - 2 * b) / 2);
        g.userData.shared = true;
        geoCache.set(k, g);
        return g;
      };
      const box = (n, w, h, d, mat, x = 0, y = 0, z = 0) => {
        const flat = true; // eckige Kanten gewünscht; roundedGeo bleibt für spätere Nutzung
        const m = new THREE.Mesh(flat ? new THREE.BoxGeometry(w, h, d) : roundedGeo(w, h, d), mat);
        m.name = n; m.position.set(x, y, z); return m;
      };
      const cyl = (n, rt, rb, h, mat, seg = 24) => {
        const m = new THREE.Mesh(new THREE.CylinderGeometry(rt, rb, h, seg), mat);
        m.name = n; return m;
      };
      const mc4 = (n, len) => {
        const g = new THREE.Group(); g.name = n;
        const body = cyl(n + '_koerper', 0.0092, 0.0104, len * 0.6, M.housing, 18); body.position.y = len * 0.06; g.add(body);
        const nut = cyl(n + '_mutter', 0.0128, 0.0128, len * 0.22, M.housing, 8); nut.position.y = -len * 0.33; g.add(nut);
        const grip = cyl(n + '_griff', 0.0108, 0.0108, len * 0.1, M.housing, 6); grip.position.y = len * 0.3; g.add(grip);
        const tip = cyl(n + '_tuelle', 0.0058, 0.0082, len * 0.16, M.housing, 14); tip.position.y = len * 0.44; g.add(tip);
        return g;
      };
      const label = (n, tex, w, h, x, y, z, ry = 0) => {
        const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h),
          new THREE.MeshStandardMaterial({ map: tex, transparent: true, roughness: 0.5, metalness: 0, polygonOffset: true, polygonOffsetFactor: -2 }));
        m.name = n; m.position.set(x, y, z); m.rotation.y = ry; m.castShadow = false;
        return m;
      };
      const tie = (n, center, r, sx) => {
        const m = new THREE.Mesh(new THREE.TorusGeometry(r, 0.0032, 6, 28), M.cable);
        m.name = n; m.rotation.x = Math.PI / 2; m.scale.set(sx, 1, 1);
        m.position.copy(center); return m;
      };
      const tube = (n, pts, r, mat) => {
        const curve = new THREE.CatmullRomCurve3(pts.map((p) => new THREE.Vector3(p[0], p[1], p[2])));
        const m = new THREE.Mesh(new THREE.TubeGeometry(curve, Math.max(28, pts.length * 12), r, 12, false), mat);
        m.name = n; return m;
      };

      const root = new THREE.Group(); root.name = 'balkonkraftwerk';
      const model = new THREE.Group(); model.name = 'anlage';
      root.add(model);
      scene.add(root);

      const railZ = 0.6, railTop = 1.04, railMid = 0.52, TILT = -26 * Math.PI / 180;
      const WALL_Z = -0.59, PANEL_MID = -0.2;
      const posts = [-1.93, -1.05, -0.2, 0.65, 1.42, 1.93];

      const arch = new THREE.Group(); arch.name = 'balkon';
      arch.add(box('bodenplatte', 3.95, 0.11, 1.4, M.concrete, 0.02, -0.055, 0));
      const wallMesh = box('hauswand', 3.95, 1.2, 0.1, M.wall, 0.02, 0.6, -0.64);
      arch.add(wallMesh);
      /* Oberseite und Stirnseiten zeigen die Ziegel, die an der Kante der Vorderseite liegen:
         oben → oberste sichtbare Steinreihe, seitlich → Steinenden jeder Reihe. */
      if (TXI.bricks) {
        const { w: tw, h: th, rows } = TXI.bricks;
        let vTop = 0.995;
        for (const [y0, y1] of rows) {
          const a = Math.max(0, y0), b = Math.min(th, y1);
          if (b - a >= 3 && (vTop === 0.995 || a < (1 - vTop) * th)) vTop = 1 - ((a + b) / 2) / th;
        }
        const uL = 3 / tw, uR = 1 - 3 / tw;
        const geo = wallMesh.geometry, pos = geo.attributes.position, uv = geo.attributes.uv;
        const W = 3.95, H = 1.2;
        for (let i = 0; i < pos.count; i++) {
          const face = Math.floor(i / 4);
          const px = pos.getX(i), py = pos.getY(i);
          if (face === 0) uv.setXY(i, uR, (py + H / 2) / H);          // rechts
          else if (face === 1) uv.setXY(i, uL, (py + H / 2) / H);     // links
          else if (face === 2) uv.setXY(i, (px + W / 2) / W, vTop);   // oben
        }
        uv.needsUpdate = true;
      }
      arch.add(box('steckdose_rahmen', 0.11, 0.11, 0.022, M.batteryTrim, -0.55, 0.95, -0.582));
      arch.add(box('steckdose_einsatz', 0.075, 0.075, 0.008, M.housing, -0.55, 0.95, -0.568));
      model.add(arch);

      const rail = new THREE.Group(); rail.name = 'gelaender';
      [railTop, railMid].forEach((y, i) => {
        const t = cyl('handlauf_' + (i + 1), 0.021, 0.021, 3.96, M.steel);
        t.rotation.z = Math.PI / 2; t.position.set(0, y, railZ);
        rail.add(t);
      });
      posts.forEach((x, i) => {
        const p = cyl('pfosten_' + (i + 1), 0.016, 0.016, railTop, M.steel, 16);
        p.position.set(x, railTop / 2, railZ);
        rail.add(p);
      });
      rail.add(box('gelaender_fussleiste', 3.96, 0.04, 0.03, M.steel, 0, 0.03, railZ));
      model.add(rail);

      function makePanel(v, name) {
        const g = new THREE.Group(); g.name = name;
        const PW = v.w, PH = v.h, PT = v.t;
        g.add(box(name + '_glas', PW - 0.06, PH - 0.06, PT * 0.7, M[v.glass], 0, 0, 0.002));
        g.add(box(name + '_rahmen_oben',   PW, 0.03, PT, M[v.frame], 0,  PH / 2 - 0.015, 0));
        g.add(box(name + '_rahmen_unten',  PW, 0.03, PT, M[v.frame], 0, -PH / 2 + 0.015, 0));
        g.add(box(name + '_rahmen_links',  0.03, PH, PT, M[v.frame], -PW / 2 + 0.015, 0, 0));
        g.add(box(name + '_rahmen_rechts', 0.03, PH, PT, M[v.frame],  PW / 2 - 0.015, 0, 0));
        const gap = 0.009, usableW = PW - 0.1, usableH = PH - 0.1;
        const cw = usableW / v.cols - gap, ch = usableH / v.rows - gap;
        const cellGeo = new THREE.BoxGeometry(cw, ch, 0.004);
        for (let i = 0; i < v.cols; i++) {
          for (let j = 0; j < v.rows; j++) {
            const c = new THREE.Mesh(cellGeo, M[v.cell]);
            c.name = name + '_zelle';
            c.position.set(-usableW / 2 + cw / 2 + gap / 2 + i * (cw + gap),
                           -usableH / 2 + ch / 2 + gap / 2 + j * (ch + gap), PT * 0.35 + 0.003);
            g.add(c);
          }
        }
        for (let b = 0; b < v.busbars; b++) {
          g.add(box(name + '_busbar', usableW, 0.006, 0.002, M.busbar, 0,
            -usableH / 2 + usableH * (b + 1) / (v.busbars + 1), PT * 0.35 + 0.006));
        }
        g.add(box(name + '_anschlussdose', 0.14, 0.08, 0.03, M.housing, 0, -PH / 2 + 0.18, -PT * 0.5 - 0.015));
        const stubA = mc4(name + '_mc4_out', 0.045);
        stubA.rotation.x = Math.PI / 2;
        stubA.position.set(0.045, -PH / 2 + 0.18, -PT * 0.5 - 0.05);
        const stubB = mc4(name + '_mc4_in', 0.045);
        stubB.rotation.x = Math.PI / 2;
        stubB.position.set(-0.045, -PH / 2 + 0.18, -PT * 0.5 - 0.05);
        g.add(stubA); g.add(stubB);
        g.userData = { out: stubA, in: stubB };
        return g;
      }

      function makeTrinaPanel(v, name) {
        const g = new THREE.Group(); g.name = name;
        const PW = v.w, PH = v.h, PT = v.t, fr = 0.026;
        g.add(box(name + '_glas', PW - fr * 2 + 0.006, PH - fr * 2 + 0.006, PT * 0.6, M.glassDark));
        g.add(box(name + '_rahmen_oben',   PW, fr, PT, M[v.frame || 'frameBlack'], 0,  PH / 2 - fr / 2, 0));
        g.add(box(name + '_rahmen_unten',  PW, fr, PT, M[v.frame || 'frameBlack'], 0, -PH / 2 + fr / 2, 0));
        g.add(box(name + '_rahmen_links',  fr, PH - fr * 2, PT, M[v.frame || 'frameBlack'], -PW / 2 + fr / 2, 0, 0));
        g.add(box(name + '_rahmen_rechts', fr, PH - fr * 2, PT, M[v.frame || 'frameBlack'],  PW / 2 - fr / 2, 0, 0));

        const cols = v.cols || 6, rowsPerHalf = v.rowsPerHalf || 11;
        const matFrame = M[v.frame || 'frameBlack'];
        const matCell = M[v.cell || 'cellTrina'];
        const matGrid = M[v.grid || 'gridWhite'];
        const gridW = v.gridWidth || 0.0035;
        const innerW = PW - fr * 2 - 0.016, innerH = PH - fr * 2 - 0.016;
        const colGap = 0.009, midGap = 0.016, rowGap = 0.0035;
        const cw = (innerW - (cols - 1) * colGap) / cols;
        const halfH = (innerH - midGap) / 2;
        const ch = (halfH - (rowsPerHalf - 1) * rowGap) / rowsPerHalf;
        const zc = PT * 0.3 + 0.003;
        const cellGeo = new THREE.BoxGeometry(cw, ch, 0.0035);
        for (let c = 0; c < cols; c++) {
          const x = -innerW / 2 + cw / 2 + c * (cw + colGap);
          for (const half of [-1, 1]) {
            for (let r = 0; r < rowsPerHalf; r++) {
              const m = new THREE.Mesh(cellGeo, matCell);
              m.name = name + '_halbzelle';
              m.position.set(x, half * (midGap / 2 + ch / 2 + r * (ch + rowGap)), zc);
              g.add(m);
            }
          }
        }
        // Zellverbinder: senkrechte Bändchen zwischen den Spalten, Trennfuge in der Mitte
        for (let c = 1; c < cols; c++) {
          g.add(box(name + '_zellverbinder', gridW, innerH, 0.0015, matGrid,
            -innerW / 2 + c * (cw + colGap) - colGap / 2, 0, zc + 0.003));
        }
        g.add(box(name + '_mittelfuge', innerW, gridW, 0.0015, matGrid, 0, 0, zc + 0.003));
        // feine Querlinien zwischen den Zellreihen
        for (const half of [-1, 1]) {
          for (let r = 1; r < rowsPerHalf; r++) {
            g.add(box(name + '_zelltrennung', innerW, 0.0016, 0.0012, matGrid,
              0, half * (midGap / 2 + r * (ch + rowGap) - rowGap / 2), zc + 0.0028));
          }
        }

        // drei Anschlussdosen auf der Rückseite, Leitungen nach links und rechts
        const zb = -PT / 2 - 0.009;
        [-innerW * 0.27, 0, innerW * 0.27].forEach((x, i) => {
          g.add(box(name + '_anschlussdose_' + (i + 1), 0.085, 0.042, 0.018, M.housing, x, 0, zb));
        });
        const stub = (n, sign) => {
          const c = mc4(n, 0.05);
          c.rotation.z = Math.PI / 2;
          c.position.set(sign * (PW / 2 + 0.03), 0, zb + 0.002);
          g.add(c);
          g.add(box(n + '_leitung', PW / 2 - 0.1, 0.007, 0.007, M.cable,
            sign * (PW / 4 + 0.05), 0, zb + 0.002));
          return c;
        };
        g.add(label(name + '_rueckseiten_etikett',
          labelTex(v.brand || 'Trinasolar', { w: 512, h: 320, bg: '#f4f4f2', color: v.brandColor || '#c8102e', size: 74,
            sub: [v.label + '  ·  ' + v.spec, 'Glas-Glas · IEC 61215 / 61730'], subColor: '#3a3d41' }),
          0.17, 0.106, 0, -innerH * 0.26, -PT / 2 - 0.0012, Math.PI));
        const out = stub(name + '_mc4_out', 1);
        const inn = stub(name + '_mc4_in', -1);
        g.userData = { out, in: inn, side: true };
        return g;
      }

      function buildPanels(v, count) {
        const grp = new THREE.Group(); grp.name = 'solarmodule';
        const refs = [];
        const dx = v.w / 2 + 0.012;
        const xs = count === 1 ? [PANEL_MID] : [PANEL_MID - dx, PANEL_MID + dx];
        xs.forEach((x, i) => {
          const p = (v.style === 'trina' ? makeTrinaPanel : makePanel)(v, 'modul_' + (i + 1));
          const drop = v.h / 2;
          p.position.set(x, railTop - 0.02 - Math.cos(TILT) * drop, railZ + Math.sin(-TILT) * drop + 0.09);
          p.rotation.x = TILT;
          grp.add(p); refs.push(p);
          [-0.5, 0.5].forEach((o, k) => {
            const br = box('halter_' + (i + 1) + '_' + (k + 1), 0.05, 0.075, 0.16, M.steel,
              x + o * v.w * 0.5, railTop - 0.04, railZ + 0.05);
            br.rotation.x = TILT * 0.55;
            grp.add(br);
            const hx = x + o * v.w * 0.5;
            const clamp = new THREE.Mesh(new THREE.TorusGeometry(0.028, 0.0055, 8, 24, Math.PI * 1.3), M.steel);
            clamp.name = 'halter_klemme'; clamp.rotation.y = Math.PI / 2; clamp.rotation.x = -Math.PI * 0.15;
            clamp.position.set(hx, railTop, railZ);
            grp.add(clamp);
            [-0.016, 0.016].forEach((oy) => {
              const bolt = cyl('halter_schraube', 0.0065, 0.0065, 0.008, M.steel, 6);
              bolt.rotation.z = Math.PI / 2;
              bolt.position.set(hx + 0.029, railTop - 0.04 + oy, railZ + 0.05);
              grp.add(bolt);
            });
          });
        });
        grp.userData = { refs, top: railTop + 0.06 };
        return grp;
      }

      function buildInverter() {
        const g = new THREE.Group(); g.name = 'wechselrichter';
        const dc = [];
        const IW = 0.262, IH = 0.204, ID = 0.0345; // Hoymiles HMS-800W-2T
        g.add(box('wr_gehaeuse', IW - 0.014, IH, ID, M.housing));
        // waagerechte Kühlrippen über die ganze Front und Rückseite
        const fins = 26, fh = 0.0042;
        const finGeo = new THREE.BoxGeometry(IW, fh, 0.009);
        for (let i = 0; i < fins; i++) {
          const y = -IH / 2 + 0.008 + i * ((IH - 0.016) / (fins - 1));
          const a = new THREE.Mesh(finGeo, M.housing); a.name = 'wr_kuehlrippe';
          a.position.set(0, y, ID / 2 + 0.0045); g.add(a);
          const b = new THREE.Mesh(finGeo, M.housing); b.name = 'wr_kuehlrippe_rueck';
          b.position.set(0, y, -ID / 2 - 0.0045); g.add(b);
        }
        g.add(box('wr_typenschild', 0.03, 0.012, 0.003, M.housingLight, IW / 2 - 0.045, -IH / 2 + 0.03, ID / 2 + 0.011));
        g.add(label('wr_logo', labelTex('HOYMILES', { color: '#e6e7e8', size: 66, spacing: 6 }), 0.09, 0.0225, 0, IH / 2 - 0.024, ID / 2 + 0.0098));

        // Montageblech mit Griffschlitz über dem Gehäuse
        const bw = 0.15, bh = 0.062;
        g.add(box('wr_montageblech_oben', bw, 0.014, 0.005, M.frameBlack, 0, IH / 2 + bh - 0.007, 0));
        g.add(box('wr_montageblech_links', 0.016, bh, 0.005, M.frameBlack, -bw / 2 + 0.008, IH / 2 + bh / 2, 0));
        g.add(box('wr_montageblech_rechts', 0.016, bh, 0.005, M.frameBlack, bw / 2 - 0.008, IH / 2 + bh / 2, 0));
        [-1, 1].forEach((sx, i) => {
          g.add(box('wr_haltelasche_' + (i + 1), 0.022, 0.05, 0.005, M.frameBlack, sx * (bw / 2 + 0.014), IH / 2 + 0.022, 0));
        });

        // WLAN-Antenne rechts
        const ant = cyl('wr_antenne', 0.0045, 0.0045, 0.08, M.housing, 12);
        ant.position.set(IW / 2 - 0.022, IH / 2 + 0.04, -0.004);
        g.add(ant);

        // DC-Anschlussleitungen: je ein MC4-Paar links und rechts
        [-1, 1].forEach((sx) => {
          [-0.052, -0.014].forEach((y, i) => {
            const c = mc4('wr_dc_stecker', 0.048);
            c.rotation.z = Math.PI / 2;
            c.position.set(sx * (IW / 2 + 0.062), y - i * 0.004, 0);
            g.add(c); dc.push(c);
            g.add(box('wr_dc_leitung', 0.055, 0.0065, 0.0065, M.cable, sx * (IW / 2 + 0.012), y - i * 0.004, 0));
          });
        });

        // AC-Leitung mit Steckverbinder rechts (zur Steckdose hin)
        // Tülle beginnt im Gehäuse (Rippen enden bei IW/2) → kein Spalt
        const ac = cyl('wr_ac_tuelle', 0.0125, 0.0125, 0.05, M.housing, 14);
        ac.rotation.z = Math.PI / 2;
        ac.position.set(IW / 2 + 0.018, IH / 2 - 0.016, 0);
        const acTip = cyl('wr_ac_tuelle_ende', 0.0085, 0.0115, 0.014, M.housing, 14);
        acTip.rotation.z = Math.PI / 2;
        acTip.position.set(IW / 2 + 0.05, IH / 2 - 0.016, 0);
        g.add(acTip);
        g.add(ac);

        g.position.set(-1.35, 0.72, WALL_Z + ID / 2 + 0.012);
        g.userData = { dc, ac, top: 0.72 + IH / 2 + 0.07 };
        return g;
      }

      /* Speicher mit integriertem Wechselrichter: DC-Eingang hinten,
         AC-Ausgang zur Steckdose. */
      function buildBattery(kind) {
        const g = new THREE.Group(); g.name = 'speichereinheit';
        let inSock, inSock2, outSock, top;
        if (kind === 'avocado') {
          // FoxESS Avocado 22 Pro — TODO: Maße aus Datenblatt bestätigen
          const BW = 0.60, BH = 0.325, BD = 0.20, RIB = 0.115, FT = 0.026;
          top = FT + BH + 0.055;
          const cy = FT + BH / 2;
          g.add(box('akku_korpus', BW - RIB, BH, BD, M.avoSilver, RIB / 2, cy, 0));
          g.add(box('akku_front', BW - RIB - 0.012, BH - 0.012, 0.008, M.avoSilver, RIB / 2, cy, BD / 2 + 0.004));

          // Kühlkörper links: feine senkrechte Lamellen
          g.add(box('akku_kuehlkoerper', RIB, BH, BD + 0.008, M.avoDark, -BW / 2 + RIB / 2, cy, 0));
          const ribGeo = new THREE.BoxGeometry(0.004, BH - 0.018, 0.007);
          for (let i = 0; i < 17; i++) {
            const r = new THREE.Mesh(ribGeo, M.avoRib); r.name = 'akku_lamelle';
            r.position.set(-BW / 2 + 0.012 + i * ((RIB - 0.02) / 16), cy, BD / 2 + 0.0075);
            g.add(r);
            const r2 = new THREE.Mesh(ribGeo, M.avoRib); r2.name = 'akku_lamelle_rueck';
            r2.position.set(-BW / 2 + 0.012 + i * ((RIB - 0.02) / 16), cy, -BD / 2 - 0.0075);
            g.add(r2);
          }

          // übersteheder Deckel mit Wandhaken hinten links
          g.add(box('akku_deckel', BW + 0.008, 0.022, BD + 0.014, M.avoDark, 0, FT + BH + 0.011, 0));
          g.add(box('akku_wandhaken', 0.075, 0.014, 0.05, M.avoDark, -BW / 2 + 0.02, FT + BH + 0.004, -BD / 2 - 0.03));

          // schwarzes Frontband mit Blitzsymbol
          g.add(box('akku_frontband', BW - RIB - 0.012, 0.062, 0.007, M.avoDark, RIB / 2, FT + 0.048, BD / 2 + 0.006));
          g.add(box('akku_symbol_blitz', 0.016, 0.03, 0.004, M.batteryTrim, BW / 2 - 0.05, FT + 0.048, BD / 2 + 0.011));
          g.add(box('akku_status_led', 0.05, 0.007, 0.004, M.led, RIB / 2 - 0.06, FT + 0.048, BD / 2 + 0.011));

          // Logo oben links auf der Silberfläche
          const logo = cyl('akku_logo', 0.019, 0.019, 0.003, M.avoDark, 20);
          logo.rotation.x = Math.PI / 2;
          logo.position.set(RIB / 2 - (BW - RIB) / 2 + 0.055, cy + BH / 2 - 0.055, BD / 2 + 0.008);
          g.add(logo);
          g.add(label('akku_schriftzug', labelTex('FOXESS', { color: '#1b1d1f', size: 70, spacing: 8 }),
            0.11, 0.0275, logo.position.x + 0.085, logo.position.y, BD / 2 + 0.0094));
          g.add(label('akku_modell', labelTex('AVOCADO 22 PRO', { color: '#2a2d30', size: 52, weight: 500, spacing: 4 }),
            0.12, 0.03, RIB / 2 + (BW - RIB) / 2 - 0.08, FT + 0.11, BD / 2 + 0.0094));

          [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(([sx, sz], i) => {
            g.add(box('akku_fuss_' + (i + 1), 0.06, FT, 0.05, M.avoDark,
              sx * (BW / 2 - 0.06), FT / 2, sz * (BD / 2 - 0.045)));
          });

          const sockY = FT + BH * 0.42;
          inSock = mc4('akku_dc_eingang', 0.045);
          inSock.rotation.x = Math.PI / 2; inSock.position.set(-BW * 0.05, sockY, -BD / 2 - 0.02);
          inSock2 = mc4('akku_dc_eingang_2', 0.045);
          inSock2.rotation.x = Math.PI / 2; inSock2.position.set(-BW * 0.05 + 0.046, sockY, -BD / 2 - 0.02);
          outSock = cyl('akku_ac_ausgang', 0.014, 0.014, 0.04, M.housing, 14);
          outSock.rotation.x = Math.PI / 2; outSock.position.set(BW * 0.28, sockY, -BD / 2 - 0.02);
        } else {
          const BW = 0.46, BH = 0.19, BD = 0.34; top = BH * 2 + 0.09; // TODO: SigenMate
          [0, 1].forEach((k) => {
            const y = 0.035 + BH / 2 + k * (BH + 0.014);
            g.add(box('akku_modul_' + (k + 1), BW, BH, BD, M.batteryPale, 0, y, 0));
            g.add(box('akku_modul_' + (k + 1) + '_front', BW - 0.03, BH - 0.05, 0.012, M.batteryTrim, 0, y, BD / 2 + 0.006));
            g.add(box('akku_modul_' + (k + 1) + '_led', 0.05, 0.008, 0.005, M.led, BW * 0.3, y, BD / 2 + 0.014));
            if (k === 1) g.add(label('akku_schriftzug', labelTex('SIGENERGY', { color: '#5a5e63', size: 60, weight: 600, spacing: 6 }),
              0.15, 0.0375, -BW * 0.24, y, BD / 2 + 0.0128));
            for (let i = 0; i < 8; i++) {
              g.add(box('akku_kuehlschlitz', 0.006, BH - 0.07, 0.006, M.housing,
                BW / 2 + 0.002, y, -BD / 2 + 0.06 + i * 0.03));
            }
          });
          g.add(box('akku_sockel', BW + 0.02, 0.035, BD + 0.02, M.housing, 0, 0.0175, 0));
          inSock = mc4('akku_dc_eingang', 0.045);
          inSock.rotation.x = Math.PI / 2; inSock.position.set(-BW * 0.24, 0.13, -BD / 2 - 0.02);
          inSock2 = mc4('akku_dc_eingang_2', 0.045);
          inSock2.rotation.x = Math.PI / 2; inSock2.position.set(-BW * 0.24 + 0.046, 0.13, -BD / 2 - 0.02);
          outSock = cyl('akku_ac_ausgang', 0.013, 0.013, 0.04, M.housing, 14);
          outSock.rotation.x = Math.PI / 2; outSock.position.set(BW * 0.18, 0.13, -BD / 2 - 0.02);
        }
        g.add(inSock); g.add(inSock2); g.add(outSock);
        g.position.set(1.62, 0, -0.28);
        g.userData = { in: inSock, in2: inSock2, out: outSock, top };
        return g;
      }

      let panelsGrp, invGrp, batGrp, wiresGrp;
      const lp = (o, dx = 0, dy = 0, dz = 0) => {
        const v = new THREE.Vector3(); o.getWorldPosition(v); model.worldToLocal(v);
        return [v.x + dx, v.y + dy, v.z + dz];
      };
      const SOCKET = [-0.55, 0.95, -0.512];

      function buildWires() {
        model.updateMatrixWorld(true);
        const g = new THREE.Group(); g.name = 'verkabelung';
        const refs = panelsGrp.userData.refs;

        if (refs.length > 1 && refs[0].userData.side) {
          const a = lp(refs[0].userData.out), b = lp(refs[1].userData.in);
          g.add(tube('kabel_modul1_modul2', [
            a,
            lp(refs[0].userData.out, 0.025, -0.01, -0.018),
            [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2 - 0.075, (a[2] + b[2]) / 2 - 0.035],
            lp(refs[1].userData.in, -0.025, -0.01, -0.018),
            b
          ], 0.009, M.cable));
        } else if (refs.length > 1) {
          const m1 = lp(refs[0].userData.out, 0, 0, -0.02);
          const m2in = lp(refs[1].userData.in, 0, 0, -0.02);
          g.add(tube('kabel_modul1_modul2', [
            lp(refs[0].userData.out), m1,
            [m1[0] + 0.25, m1[1] - 0.055, m1[2] - 0.012],
            [(m1[0] + m2in[0]) / 2, (m1[1] + m2in[1]) / 2 - 0.085, (m1[2] + m2in[2]) / 2 - 0.018],
            [m2in[0] - 0.25, m2in[1] - 0.055, m2in[2] - 0.012],
            m2in, lp(refs[1].userData.in)
          ], 0.009, M.cable));
        }

        /* Ein PV-Strang hat zwei freie Enden — beide werden parallel verlegt. */
        const dcRun = (name, srcStub, dstStub, off) => {
          const so = lp(srcStub, 0, 0, -0.02);
          const post = posts.reduce((a, b) => (Math.abs(b - so[0]) < Math.abs(a - so[0]) ? b : a));
          const d = lp(dstStub, 0, 0, -0.018);
          const pts = [
            lp(srcStub), so,
            [so[0], so[1] - 0.035, so[2] - 0.06],
            [post + 0.03 + off, 0.2, railZ + 0.06],
            [post + 0.01 + off, 0.11, railZ - 0.04]
          ];
          if (batGrp) {
            // um die linke Gehäuseseite herum zur Rückseite — nie vor der Front entlang
            const bb = new THREE.Box3().setFromObject(batGrp);
            const lx = bb.min.x - 0.07 - off;
            pts.push(
              [post + 0.06 + off, 0.085, 0.3 - off],
              [Math.max(lx + 0.12, Math.min(post, bb.max.x) - 0.1), 0.085, bb.max.z + 0.14 - off],
              [lx, 0.085, bb.max.z + 0.06 - off],
              [lx, 0.085, bb.min.z - 0.04 - off],
              [lx + 0.1, 0.09, d[2] - 0.1 - off],
              [d[0] - 0.04, 0.12, d[2] - 0.07],
              d, lp(dstStub, 0, 0, -0.002));
          } else {
            pts.push(
              [post - 0.08 + off, 0.085, 0.22 - off],
              [-0.95, 0.085, -0.18 - off],
              [-1.42, 0.085, -0.46 - off],
              [-1.56, 0.24, -0.5 - off],
              [d[0] - 0.08, d[1] - 0.2, d[2] + 0.02],
              [d[0] - 0.05, d[1] - 0.04, d[2] + 0.01],
              d, lp(dstStub));
          }
          g.add(tube(name, pts, 0.009, M.cable));
          const curve = new THREE.CatmullRomCurve3(pts.map((p) => new THREE.Vector3(p[0], p[1], p[2])));
          [0.42, 0.26].forEach((yT, k) => {
            for (let i = 0; i <= 240; i++) {
              const p = curve.getPoint(i / 240);
              if (p.y <= yT) {
                const postPt = new THREE.Vector3(post, yT, railZ);
                const dist = p.distanceTo(postPt);
                if (dist < 0.07) {
                  const mid = p.clone().add(postPt).multiplyScalar(0.5);
                  const r = 0.022;
                  g.add(tie(name + '_kabelbinder_' + (k + 1), mid, r, Math.max(1, (dist / 2 + 0.018) / r)));
                }
                break;
              }
            }
          });
        };

        const endPlus = refs[refs.length - 1].userData.out;
        const endMinus = refs[0].userData.in;

        if (batGrp) {
          dcRun('kabel_module_speicher_plus', endPlus, batGrp.userData.in, 0);
          dcRun('kabel_module_speicher_minus', endMinus, batGrp.userData.in2, 0.032);

          const bOut = lp(batGrp.userData.out, 0, 0, -0.02);
          g.add(tube('kabel_speicher_steckdose', [
            lp(batGrp.userData.out, 0, 0, -0.002), bOut,
            [bOut[0] + 0.02, bOut[1] - 0.02, bOut[2] - 0.1],
            [bOut[0] - 0.06, 0.085, -0.58],
            [0.6, 0.085, -0.56],
            [-0.1, 0.085, -0.555],
            [-0.56, 0.2, -0.55],
            [-0.58, 0.58, -0.545],
            [-0.57, 0.88, -0.535],
            SOCKET
          ], 0.008, M.cable));
        } else {
          dcRun('kabel_module_wechselrichter_plus', endPlus, invGrp.userData.dc[1], 0);
          dcRun('kabel_module_wechselrichter_minus', endMinus, invGrp.userData.dc[0], 0.032);

          const wrAc = lp(invGrp.userData.ac, 0.06, 0, 0);
          g.add(tube('kabel_wechselrichter_steckdose', [
            lp(invGrp.userData.ac), lp(invGrp.userData.ac, 0.035, 0, 0), wrAc, lp(invGrp.userData.ac, 0.09, 0, 0),
            [wrAc[0] + 0.12, wrAc[1] + 0.03, wrAc[2] + 0.012],
            [wrAc[0] + 0.24, wrAc[1] + 0.09, wrAc[2] + 0.02],
            [-0.8, 0.92, -0.53],
            [-0.64, 0.95, -0.53],
            SOCKET
          ], 0.008, M.cable));
        }
        g.add(box('netzstecker', 0.07, 0.07, 0.05, M.housing, -0.55, 0.95, -0.539));
        return g;
      }

      /* ------------------------------------------------- Flachdach (Carport) */
      const ROOF = { w: 3.4, d: 5.0, top: 2.35, t: 0.14 };
      const PX = ROOF.w / 2 - 0.12, PZ = -(ROOF.d / 2 - 0.25);   // hinterer rechter Pfosten
      const BETA = 30 * Math.PI / 180;
      const U = new THREE.Vector3(0, Math.sin(BETA), -Math.cos(BETA));   // die Schräge hinauf
      const N = new THREE.Vector3(0, Math.cos(BETA), Math.sin(BETA));    // Modulnormale
      const SOCKET_D = [PX - 0.132, 1.1, PZ];

      const carport = new THREE.Group(); carport.name = 'carport';
      carport.add(box('carport_boden', ROOF.w + 0.9, 0.04, ROOF.d + 0.9, M.pavement, 0, 0.02, 0));
      carport.add(box('carport_dach', ROOF.w, ROOF.t, ROOF.d, M.roofTop, 0, ROOF.top - ROOF.t / 2, 0));
      [-1, 1].forEach((sz) => carport.add(box('carport_blende_' + (sz > 0 ? 'vorn' : 'hinten'),
        ROOF.w + 0.1, 0.24, 0.05, M.doorFrame, 0, ROOF.top - 0.08, sz * (ROOF.d / 2 + 0.025))));
      [-1, 1].forEach((sx) => carport.add(box('carport_blende_' + (sx > 0 ? 'rechts' : 'links'),
        0.05, 0.24, ROOF.d, M.doorFrame, sx * (ROOF.w / 2 + 0.025), ROOF.top - 0.08, 0)));
      [-1, 1].forEach((sx) => {
        carport.add(box('carport_traeger', 0.1, 0.18, ROOF.d - 0.1, M.doorFrame, sx * PX, ROOF.top - ROOF.t - 0.09, 0));
        const h = ROOF.top - ROOF.t - 0.18 - 0.04;
        [-1, 1].forEach((sz) => carport.add(box('carport_pfosten', 0.12, h, 0.12, M.doorFrame, sx * PX, 0.04 + h / 2, sz * -PZ)));
      });

      /* Je Modul: 2 Aufständerungsdreiecke 20–45° + 2 Montageschienen 1,2 m (Alu silber), Modul hochkant, 30° */
      function buildRoofArray(v, count) {
        const grp = new THREE.Group(); grp.name = 'flachdach_anlage';
        const L = v.w, W = v.h, A = 0.04, footH = 0.1;
        const Ltri = L * 0.86, depth = Ltri * Math.cos(BETA), z0 = depth / 2, zb = z0 - depth;
        const yT = ROOF.top, hR = footH + Ltri * Math.sin(BETA) - A;
        const refs = [], frames = [], cxs = [];
        for (let i = 0; i < count; i++) {
          const cx = (i - (count - 1) / 2) * (W + 0.04);
          cxs.push(cx);
          const T = new THREE.Group(); T.name = 'neigungsebene_' + (i + 1);
          T.position.set(cx, yT + footH, z0);
          T.rotation.x = -(Math.PI / 2 - BETA);
          grp.add(T); frames.push(T);

          const p = (v.style === 'trina' ? makeTrinaPanel : makePanel)(v, 'modul_' + (i + 1));
          p.rotation.z = Math.PI / 2;
          p.position.set(0, 0.02 + L / 2, A * 1.5 + v.t / 2);
          T.add(p); refs.push(p);

          [0.2, 0.8].forEach((f, k) => {
            const y = 0.02 + L * f;
            T.add(box('montageschiene_' + (i + 1) + '_' + (k + 1), 1.2, A, A, M.alu, 0, y, A));
            [-1, 1].forEach((sx) => T.add(box('endklemme', 0.025, 0.05, 0.035, M.alu, sx * (W / 2 + 0.012), y, A * 1.5 + 0.0175)));
          });

          [-0.42, 0.42].forEach((dx, k) => {
            const n = 'dreieck_' + (i + 1) + '_' + (k + 1), xw = cx + dx, side = Math.sign(dx);
            T.add(box(n + '_schraege', A, Ltri, A, M.alu, dx, Ltri / 2, 0));
            grp.add(box(n + '_basis', A, A, depth + 0.08, M.alu, xw, yT + A / 2, z0 - depth / 2));
            grp.add(box(n + '_stuetze_vorn', A, footH - A, A, M.alu, xw, yT + A + (footH - A) / 2, z0));
            grp.add(box(n + '_stuetze_hinten', A, hR, A, M.alu, xw, yT + A + hR / 2, zb));
            const by = yT + A, bz = z0 - depth * 0.4, ry = yT + A + hR * 0.62, rz = zb + A / 2;
            const len = Math.hypot(ry - by, rz - bz);
            const br = box(n + '_strebe', A * 0.8, len, A * 0.8, M.alu, xw, (by + ry) / 2, (bz + rz) / 2);
            br.rotation.x = Math.atan2(rz - bz, ry - by);
            grp.add(br);
            [[yT + footH, z0], [yT + footH + Ltri * Math.sin(BETA), zb], [ry, rz + 0.01]].forEach(([y, z]) => {
              const bolt = cyl('dreieck_schraube', 0.008, 0.008, 0.012, M.steel, 6);
              bolt.rotation.z = Math.PI / 2;
              bolt.position.set(xw + side * (A / 2 + 0.006), y, z);
              grp.add(bolt);
            });
          });
        }
        grp.userData = { refs, frames, cxs, z0, depth, top: yT + footH + Ltri * Math.sin(BETA) + 0.15 };
        return grp;
      }

      /* ------------------------------------------------- Zaun (Doppelstabmatte) */
      const FENCE = { y0: 0.08, h: 1.43, posts: [-2.5, 0, 2.5] };
      const fenceMat = new THREE.MeshStandardMaterial({ color: 0x353a3e, roughness: 0.55, metalness: 0.25 });
      const lawnMat = new THREE.MeshStandardMaterial({ color: 0x7a8c58, roughness: 1, metalness: 0 });
      const fenceRows = Array.from({ length: 8 }, (_, k) => FENCE.y0 + 0.02 + k * 0.2);
      const fence = new THREE.Group(); fence.name = 'zaun';
      fence.add(box('rasen', 5.9, 0.04, 2.6, lawnMat, 0, 0.02, 0.3));
      FENCE.posts.forEach((x, i) => {
        const ph = FENCE.h + 0.12;
        fence.add(box('zaunpfosten_' + (i + 1), 0.06, ph, 0.04, fenceMat, x, 0.04 + ph / 2, -0.032));
        fence.add(box('pfostenkappe_' + (i + 1), 0.064, 0.008, 0.044, fenceMat, x, 0.04 + ph + 0.004, -0.032));
        // Mattenhalter: an den Endpfosten nur zur Mattenseite hin, sonst ragen sie ins Leere
        const end = i === 0 ? 1 : i === FENCE.posts.length - 1 ? -1 : 0;
        [1, 4, 7].forEach((r) => fence.add(box('mattenhalter', end ? 0.055 : 0.09, 0.03, 0.006, fenceMat, x + end * 0.0175, fenceRows[r], 0.015)));
      });
      const barGeo = new THREE.BoxGeometry(0.006, FENCE.h, 0.006);
      for (let f = 0; f < FENCE.posts.length - 1; f++) {
        const xa = FENCE.posts[f] + 0.045, xb = FENCE.posts[f + 1] - 0.045, len = xb - xa;
        for (let x = xa + 0.01; x <= xb - 0.01; x += 0.05) {
          const m = new THREE.Mesh(barGeo, fenceMat); m.name = 'zaun_senkrechtstab';
          m.position.set(x, FENCE.y0 + FENCE.h / 2, 0); fence.add(m);
        }
        fenceRows.forEach((y) => [-1, 1].forEach((sz) =>
          fence.add(box('zaun_doppeldraht', len, 0.008, 0.008, fenceMat, (xa + xb) / 2, y, sz * 0.007))));
      }

      /* Je Modul: 2 Schienen 1,2 m senkrecht, 4 Flachverbinder hinter der Matte, 4 Endklemmen, 4 Rechteckkappen */
      function buildFenceArray(v, count) {
        const grp = new THREE.Group(); grp.name = 'zaun_anlage';
        const A = 0.04, RL = 1.2, rz = 0.011 + A / 2, zc = rz + A / 2 + v.t / 2, zFront = zc + v.t / 2;
        const ym = FENCE.y0 + 0.7;
        const refs = [], cxs = [], rxs = [];
        const nearRow = (y) => fenceRows.reduce((a, b) => (Math.abs(b - y) < Math.abs(a - y) ? b : a));
        for (let i = 0; i < count; i++) {
          const cx = count === 1 ? 0 : (i - 0.5) * (v.w + 0.024);
          cxs.push(cx);
          const p = (v.style === 'trina' ? makeTrinaPanel : makePanel)(v, 'modul_' + (i + 1));
          p.position.set(cx, ym, zc);
          // MC4-Stecker seitlich anlegen statt nach hinten — sonst stechen sie durch die Zaunmatte
          if (!p.userData.side) [p.userData.out, p.userData.in].forEach((st) => {
            st.rotation.set(0, 0, Math.PI / 2); st.position.z = -v.t * 0.5 - 0.015;
            st.position.x = Math.sign(st.position.x) * 0.1;
          });
          grp.add(p); refs.push(p);
          [-1, 1].forEach((sx, k) => {
            const rx = cx + sx * v.w * 0.27, n = (i + 1) + '_' + (k + 1);
            rxs.push(rx);
            grp.add(box('montageschiene_' + n, A, RL, A, M.frameBlack, rx, ym, rz));
            [-1, 1].forEach((sy) => {
              grp.add(box('rechteckkappe_' + n, 0.042, 0.006, 0.042, M.housing, rx, ym + sy * (RL / 2 + 0.003), rz));
              // Endklemme: Körper neben dem Rahmen, Lippe über der Modulkante, Schraube
              const ye = ym + sy * v.h / 2;
              grp.add(box('endklemme_' + n, 0.04, 0.012, v.t, M.frameBlack, rx, ye + sy * 0.006, zc));
              grp.add(box('endklemme_lippe_' + n, 0.04, 0.021, 0.004, M.frameBlack, rx, ye + sy * 0.0015, zFront + 0.002));
              const sc = cyl('endklemme_schraube', 0.0055, 0.0055, 0.004, M.steel, 6);
              sc.rotation.x = Math.PI / 2; sc.position.set(rx, ye + sy * 0.006, zFront + 0.006); grp.add(sc);
              // Flachverbinder hinter dem Doppeldraht, verschraubt in die Schienennut
              const wy = nearRow(ym + sy * 0.5);
              grp.add(box('flachverbinder_' + n, 0.04, 0.12, 0.004, M.steel, rx, wy, -0.013));
              [-0.045, -0.018, 0.018, 0.045].forEach((oy) => {
                const bolt = Math.abs(oy) < 0.03;
                const c = cyl(bolt ? 'flachverbinder_schraube' : 'flachverbinder_loch', bolt ? 0.0065 : 0.0042, bolt ? 0.0065 : 0.0042,
                  bolt ? 0.005 : 0.0012, bolt ? M.steel : M.cable, bolt ? 6 : 12);
                c.rotation.x = Math.PI / 2; c.position.set(rx, wy + oy, bolt ? -0.0175 : -0.0156); grp.add(c);
              });
            });
          });
        }
        grp.userData = { refs, cxs, rxs, ym, rz, top: ym + v.h / 2 + 0.15 };
        return grp;
      }

      function buildFenceModuleLink() {
        model.updateMatrixWorld(true);
        const g = new THREE.Group(); g.name = 'verkabelung';
        const refs = panelsGrp.userData.refs;
        if (refs.length > 1) {
          const a = lp(refs[0].userData.out), b = lp(refs[1].userData.in);
          const m = [(a[0] + b[0]) / 2, Math.min(a[1], b[1]) - 0.1, (a[2] + b[2]) / 2];
          g.add(tube('kabel_modul1_modul2', [a, [a[0], a[1] - 0.04, a[2]], m, [b[0], b[1] - 0.04, b[2]], b], 0.009, M.cable));
        }
        return g;
      }

      /* ------------------------------------------------- Trapezdach (Garage, Pultdach) */
      const TR = { w: 3.2, d: 6.0, hb: 2.1, base: 0.04, p: 0.207, c: 0.04, s: 0.022, h: 0.035, n: 17 };
      TR.hf = TR.hb + TR.d * Math.tan(Math.atan(0.45 / 6.0) + 10 * Math.PI / 180);   // bisherige Neigung (~4,3°) + 10°
      const trAng = Math.atan((TR.hf - TR.hb) / TR.d), trTrough = TR.p - TR.c - 2 * TR.s;
      const SW = TR.n * TR.p + trTrough, SL = (TR.d + 0.5) / Math.cos(trAng);
      const crestX = Array.from({ length: TR.n }, (_, i) => -SW / 2 + i * TR.p + trTrough + TR.s + TR.c / 2);
      const trMat = {
        sheet: new THREE.MeshStandardMaterial({ color: 0x8e949b, roughness: 0.36, metalness: 0.65 }),
        wall: new THREE.MeshStandardMaterial({ color: 0xdcdcd8, roughness: 0.85, metalness: 0 }),
        flash: new THREE.MeshStandardMaterial({ color: 0xb9bdc1, roughness: 0.3, metalness: 0.7 }),
        door: new THREE.MeshStandardMaterial({ color: 0x4a4f55, roughness: 0.5, metalness: 0.3 }),
        timber: new THREE.MeshStandardMaterial({ color: 0x8a6a4a, roughness: 0.8, metalness: 0 })
      };
      const trRoofPos = new THREE.Vector3(0, TR.base + (TR.hf + TR.hb) / 2 + 0.06, 0);
      const garage = new THREE.Group(); garage.name = 'garage_trapezdach';
      garage.add(box('garage_boden', TR.w + 1.8, 0.04, TR.d + 1.8, M.pavement, 0, 0.02, 0));
      {
        const sh = new THREE.Shape();
        sh.moveTo(TR.d / 2, 0); sh.lineTo(-TR.d / 2, 0); sh.lineTo(-TR.d / 2, TR.hf); sh.lineTo(TR.d / 2, TR.hb); sh.closePath();
        const g = new THREE.ExtrudeGeometry(sh, { depth: 0.2, bevelEnabled: false });
        [-TR.w / 2, TR.w / 2 - 0.2].forEach((x, i) => {
          const m = new THREE.Mesh(g, trMat.wall); m.name = 'garage_seitenwand_' + (i + 1);
          m.rotation.y = Math.PI / 2; m.position.set(x, TR.base, 0); garage.add(m);
        });
        garage.add(box('garage_frontwand_oben', TR.w - 0.4, TR.hf - 2.15, 0.2, trMat.wall, 0, TR.base + 2.15 + (TR.hf - 2.15) / 2, TR.d / 2 - 0.1));
        garage.add(box('garage_rueckwand', TR.w - 0.4, TR.hb, 0.2, trMat.wall, 0, TR.base + TR.hb / 2, -TR.d / 2 + 0.1));
        garage.add(box('garage_tor', TR.w - 0.4, 2.15, 0.03, trMat.door, 0, TR.base + 1.075, TR.d / 2 - 0.12));
        for (let k = 1; k < 5; k++) garage.add(box('garage_tor_fuge', TR.w - 0.4, 0.01, 0.006, M.housing, 0, TR.base + k * 0.43, TR.d / 2 - 0.104));

        const roof = new THREE.Group(); roof.name = 'trapezdach';
        roof.position.copy(trRoofPos); roof.rotation.x = -trAng;
        const ps = new THREE.Shape();
        ps.moveTo(-SW / 2, 0);
        for (let i = 0; i < TR.n; i++) {
          const x0 = -SW / 2 + i * TR.p;
          ps.lineTo(x0 + trTrough, 0); ps.lineTo(x0 + trTrough + TR.s, TR.h);
          ps.lineTo(x0 + trTrough + TR.s + TR.c, TR.h); ps.lineTo(x0 + TR.p, 0);
        }
        ps.lineTo(SW / 2, 0); ps.lineTo(SW / 2, -0.004); ps.lineTo(-SW / 2, -0.004); ps.closePath();
        const sg = new THREE.ExtrudeGeometry(ps, { depth: SL, bevelEnabled: false });
        sg.translate(0, 0, -SL / 2);
        const sheet = new THREE.Mesh(sg, trMat.sheet); sheet.name = 'trapezblech'; roof.add(sheet);
        const zc = TR.d / 2 / Math.cos(trAng) - 0.1;
        [-zc, 0, zc].forEach((z, i) => roof.add(box('pfette_' + (i + 1), TR.w, 0.06, 0.1, trMat.timber, 0, -0.034, z)));
        [-1, 1].forEach((sx) => roof.add(box('ortgangblech', 0.03, 0.13, SL + 0.03, trMat.flash, sx * (SW / 2 + 0.015), 0.005, 0)));
        roof.add(box('stirnblech', SW + 0.06, 0.16, 0.03, trMat.flash, 0, -0.03, SL / 2 + 0.015));
        roof.add(box('dachrinne', SW, 0.08, 0.1, trMat.flash, 0, -0.06, -SL / 2 - 0.05));
        garage.add(roof);
      }

      /* Je Modul hochkant: 4 Trapezprofil-Schienen 400 mm auf den Hochsicken, quer 2 Alu-Schienen 1,2 m,
         4 Flachverbinder mit Hammerkopfschrauben M10 + Sperrzahnmuttern, 4 Endklemmen, 4 Rechteckkappen */
      function buildTrapezArray(v, count) {
        const grp = new THREE.Group(); grp.name = 'trapezdach_anlage';
        grp.position.copy(trRoofPos); grp.rotation.x = -trAng;
        const T = new THREE.Group(); T.name = 'dachebene'; T.rotation.x = -Math.PI / 2; grp.add(T);
        const A = 0.04, RH = 0.03, z1 = TR.h + 0.003 + RH, z2 = z1 + 0.004, zr = z2 + A / 2;
        const zc = z2 + A + v.t / 2, zFront = zc + v.t / 2;
        const refs = [], rails = [];
        const nearCrest = (x) => crestX.reduce((a, b) => (Math.abs(b - x) < Math.abs(a - x) ? b : a));
        for (let i = 0; i < count; i++) {
          // 1,2-m-Schienen je Modul dürfen sich nicht überlappen: Modulabstand = Schienenlänge + Kappen + Luft
          const cx = count === 1 ? 0 : (i - 0.5) * (1.2 + 0.02);
          const p = (v.style === 'trina' ? makeTrinaPanel : makePanel)(v, 'modul_' + (i + 1));
          p.rotation.z = Math.PI / 2; p.position.set(cx, 0, zc);
          T.add(p); refs.push(p);
          [-1, 1].forEach((sy, j) => {
            const ry = sy * v.w * 0.25, rn = (i + 1) + '_' + (j + 1);
            const r = box('montageschiene_' + rn, 1.2, A, A, M.frameBlack, cx, ry, zr);
            T.add(r); rails.push(r);
            [-1, 1].forEach((sx) => {
              T.add(box('rechteckkappe_' + rn, 0.006, 0.042, 0.042, M.housing, cx + sx * 0.603, ry, zr));
              const xe = cx + sx * v.h / 2;
              T.add(box('endklemme_' + rn, 0.012, 0.04, v.t, M.frameBlack, xe + sx * 0.006, ry, zc));
              T.add(box('endklemme_lippe_' + rn, 0.021, 0.04, 0.004, M.frameBlack, xe + sx * 0.0015, ry, zFront + 0.002));
              const sc = cyl('endklemme_schraube', 0.0055, 0.0055, 0.004, M.steel, 6);
              sc.rotation.x = Math.PI / 2; sc.position.set(xe + sx * 0.006, ry, zFront + 0.006); T.add(sc);

              const rx = nearCrest(cx + sx * v.h * 0.28), n = rn + '_' + (sx < 0 ? 1 : 2);
              T.add(box('trapez_epdm_' + n, 0.05, 0.39, 0.003, M.cable, rx, ry, TR.h + 0.0015));
              T.add(box('trapezschiene_' + n, 0.05, 0.4, RH, M.alu, rx, ry, TR.h + 0.003 + RH / 2));
              T.add(box('flachverbinder_' + n, 0.04, 0.14, 0.004, M.steel, rx, ry, z1 + 0.002));
              [-0.05, 0.05].forEach((oy) => {
                const nut = cyl('sperrzahnmutter_m10', 0.0095, 0.0095, 0.008, M.steel, 6);
                nut.rotation.x = Math.PI / 2; nut.position.set(rx, ry + oy, z2 + 0.004); T.add(nut);
                const fl = cyl('sperrzahnmutter_flansch', 0.011, 0.011, 0.002, M.steel, 18);
                fl.rotation.x = Math.PI / 2; fl.position.set(rx, ry + oy, z2 + 0.001); T.add(fl);
                const tip = cyl('hammerkopfschraube_m10', 0.005, 0.005, 0.006, M.steel, 12);
                tip.rotation.x = Math.PI / 2; tip.position.set(rx, ry + oy, z2 + 0.011); T.add(tip);
              });
            });
          });
        }
        grp.userData = { refs, rails };
        return grp;
      }

      function buildTrapezModuleLink() {
        model.updateMatrixWorld(true);
        const g = new THREE.Group(); g.name = 'verkabelung';
        const refs = panelsGrp.userData.refs;
        if (refs.length > 1) {
          const a = lp(refs[0].userData.out), b = lp(refs[1].userData.in);
          const m = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2 - 0.06];
          g.add(tube('kabel_modul1_modul2', [a, m, b], 0.009, M.cable));
        }
        return g;
      }

      // Flachdach zeigt nur Module: einzig die Verbindung zwischen zwei Modulen
      function buildRoofModuleLink() {
        model.updateMatrixWorld(true);
        const g = new THREE.Group(); g.name = 'verkabelung';
        const refs = panelsGrp.userData.refs;
        if (refs.length > 1) {
          const add = (p, du, dn) => [p[0] + U.x * du + N.x * dn, p[1] + U.y * du + N.y * dn, p[2] + U.z * du + N.z * dn];
          const a = lp(refs[0].userData.out), b = lp(refs[1].userData.in);
          const m = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
          g.add(tube('kabel_modul1_modul2', [a, add(a, 0.04, 0), add(a, 0.06, -0.08),
            add(m, 0, -0.1), add(b, -0.06, -0.08), add(b, -0.04, 0), b], 0.009, M.cable));
        }
        return g;
      }

      function buildRoofWires() {
        model.updateMatrixWorld(true);
        const g = new THREE.Group(); g.name = 'verkabelung';
        const ud = panelsGrp.userData, refs = ud.refs, zb = ud.z0 - ud.depth;
        const add = (p, du, dn) => [p[0] + U.x * du + N.x * dn, p[1] + U.y * du + N.y * dn, p[2] + U.z * du + N.z * dn];
        const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];

        if (refs.length > 1) {
          const a = lp(refs[0].userData.out), b = lp(refs[1].userData.in);
          g.add(tube('kabel_modul1_modul2', [a, add(a, 0.04, 0), add(a, 0.06, -0.08),
            add(mid(a, b), 0, -0.1), add(b, -0.06, -0.08), add(b, -0.04, 0), b], 0.009, M.cable));
        }
        const endA = refs[0].userData.in, endB = refs[refs.length - 1].userData.out;   // Strangende unten / oben

        // vom Modulstecker unter das Modul auf die Dachhaut und nach hinten
        const fromStub = (stub, sgn, k) => {
          const E = lp(stub), e1 = add(E, sgn * 0.04, 0), e2 = add(E, sgn * 0.06, -0.08);
          const xr = E[0] + 0.12 + k * 0.03;
          const p3 = [xr, ROOF.top + 0.05, sgn < 0 ? e2[2] - 0.25 : e2[2] - 0.08];
          return [E, e1, e2, p3, [xr + 0.05, ROOF.top + 0.028, Math.min(p3[2] - 0.15, zb - 0.25)]];
        };
        // über die hintere Blende und am Pfosten herunter
        const toPost = (k, zo) => {
          const x = PX - 0.085 - k * 0.025, ze = -ROOF.d / 2;
          return [
            [PX - 0.32 - k * 0.025, ROOF.top + 0.028, ze + 0.3],
            [x, ROOF.top + 0.03, ze + 0.1],
            [x, ROOF.top + 0.075, ze - 0.005],
            [x, ROOF.top + 0.06, ze - 0.065],
            [x, ROOF.top - 0.24, ze - 0.08],
            [PX - 0.085, ROOF.top - 0.36, ze + 0.05],
            [PX - 0.085, ROOF.top - 0.5, PZ + zo]
          ];
        };

        if (invGrp) {
          const C = lp(invGrp);
          const toDc = (stub, sgn, dc) => {
            const E = lp(stub), D = lp(dc), sx = Math.sign(D[0] - C[0]) || 1;
            const e1 = add(E, sgn * 0.04, 0), e2 = add(E, sgn * 0.06, -0.08);
            const a1 = [D[0] + sx * 0.05, D[1], D[2]], a2 = add([D[0] + sx * 0.12, D[1], D[2]], 0, -0.03);
            return [E, e1, e2, add(mid(e2, a2), 0, -0.03), a2, a1, D];
          };
          g.add(tube('kabel_module_wechselrichter_minus', toDc(endA, -1, invGrp.userData.dc[0]), 0.009, M.cable));
          g.add(tube('kabel_module_wechselrichter_plus', toDc(endB, 1, invGrp.userData.dc[1]), 0.009, M.cable));
          const E0 = lp(invGrp.userData.ac), sx = Math.sign(E0[0] - C[0]) || -1;
          g.add(tube('kabel_wechselrichter_steckdose', [
            E0, [E0[0] + sx * 0.035, E0[1], E0[2]], [E0[0] + sx * 0.06, E0[1], E0[2]],
            add([E0[0] + sx * 0.1, E0[1], E0[2]], 0, -0.06),
            [E0[0] + sx * 0.16, ROOF.top + 0.05, E0[2] - 0.15],
            [E0[0] + sx * 0.1, ROOF.top + 0.028, zb - 0.25],
            ...toPost(0, 0),
            [PX - 0.085, 1.32, PZ], [PX - 0.13, 1.17, PZ], SOCKET_D
          ], 0.008, M.cable));
        } else {
          const toBat = (sock, zo) => {
            const d = lp(sock, 0, 0, -0.018);
            return [[PX - 0.085, 0.16, PZ + zo], [PX - 0.22, 0.1, PZ + zo + 0.05], [d[0], 0.1, d[2] - 0.12], d, lp(sock, 0, 0, -0.002)];
          };
          g.add(tube('kabel_module_speicher_minus', [...fromStub(endA, -1, 1), ...toPost(1, 0.03), ...toBat(batGrp.userData.in2, 0.03)], 0.009, M.cable));
          g.add(tube('kabel_module_speicher_plus', [...fromStub(endB, 1, 2), ...toPost(2, 0.06), ...toBat(batGrp.userData.in, 0.06)], 0.009, M.cable));
          const o = lp(batGrp.userData.out, 0, 0, -0.02);
          g.add(tube('kabel_speicher_steckdose', [
            lp(batGrp.userData.out, 0, 0, -0.002), o, [o[0], 0.1, o[2] - 0.12],
            [PX - 0.22, 0.1, PZ - 0.06], [PX - 0.085, 0.22, PZ - 0.03], [PX - 0.085, 0.95, PZ - 0.03],
            [PX - 0.13, 1.03, PZ], SOCKET_D
          ], 0.008, M.cable));
        }
        g.add(box('netzstecker', 0.05, 0.07, 0.07, M.housing, PX - 0.107, 1.1, PZ));
        return g;
      }

      const self = this;
      function rebuild() {
        [panelsGrp, invGrp, batGrp, wiresGrp].forEach((g) => {
          if (!g) return;
          if (g.parent) g.parent.remove(g);
          g.traverse((o) => { if (o.isMesh && !o.geometry.userData.shared) o.geometry.dispose(); });
        });
        invGrp = null; batGrp = null;
        const s = SETS[self._cfg.set];
        const dach = self._cfg.mount === 'dach', zaun = self._cfg.mount === 'zaun', trapez = self._cfg.mount === 'trapez';
        // Szenen tauschen (entfernen statt verstecken — Bounding-Box zählt auch Unsichtbares)
        [arch, rail].forEach((o) => (dach || zaun || trapez ? model.remove(o) : model.add(o)));
        if (dach) model.add(carport); else model.remove(carport);
        if (zaun) model.add(fence); else model.remove(fence);
        if (trapez) model.add(garage); else model.remove(garage);
        if (trapez) {
          panelsGrp = buildTrapezArray(s.panel, self._cfg.modules);
          model.add(panelsGrp);
          wiresGrp = buildTrapezModuleLink();
        } else if (zaun) {
          panelsGrp = buildFenceArray(s.panel, self._cfg.modules);
          model.add(panelsGrp);
          wiresGrp = buildFenceModuleLink();
        } else if (dach) {
          panelsGrp = buildRoofArray(s.panel, self._cfg.modules);
          model.add(panelsGrp);
          wiresGrp = buildRoofModuleLink();
        } else {
          panelsGrp = buildPanels(s.panel, self._cfg.modules);
          model.add(panelsGrp);
          if (s.battery) { batGrp = buildBattery(s.battery.kind); model.add(batGrp); }
          else { invGrp = buildInverter(); model.add(invGrp); }
          wiresGrp = buildWires();
        }
        model.add(wiresGrp);
        model.traverse((o) => { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } });
        updateAnchors();
        frame();
      }

      /* ------------------------------------------------------------ Hotspots */
      const specs = [
        { key: 'panel', label: 'Solarmodule' },
        { key: 'mount', label: 'Aufständerung' },
        { key: 'inverter', label: 'Wechselrichter' },
        { key: 'battery', label: 'Speichereinheit' },
        { key: 'socket', label: 'Steckdose' }
      ];
      const hotspots = specs.map((hs) => {
        const anchor = new THREE.Object3D();
        model.add(anchor);
        const el = document.createElement('div');
        el.className = 'hs';
        el.innerHTML = '<div class="tip"><b></b><span></span></div>' +
          '<button class="dot" type="button" aria-label="Info: ' + hs.label + '">?</button>';
        el.querySelector('b').textContent = hs.label;
        const hold = () => { el.dataset.held = '1'; orbit.state.autoRotate = false; };
        const release = () => {
          delete el.dataset.held;
          if (!self._layer.querySelector('.hs[data-held]') && !orbit.state.used && self._wantsRotate) {
            orbit.state.autoRotate = true;
          }
        };
        el.addEventListener('pointerenter', hold);
        el.addEventListener('focusin', hold);
        el.addEventListener('pointerleave', release);
        el.addEventListener('focusout', release);
        self._layer.appendChild(el);
        return Object.assign({}, hs, { anchor, el, tip: el.querySelector('span') });
      });

      function updateAnchors() {
        const s = SETS[self._cfg.set];
        const dach = self._cfg.mount === 'dach', zaun = self._cfg.mount === 'zaun', trapez = self._cfg.mount === 'trapez';
        const at = {};
        if (trapez) {
          const ud = panelsGrp.userData, pc = lp(ud.refs[ud.refs.length - 1]), rc = lp(ud.rails[0]);
          at.panel = [pc[0] + 0.3, pc[1] + 0.55, pc[2] + 0.2];
          at.mount = [rc[0] - 0.66, rc[1] + 0.12, rc[2]];
        } else if (zaun) {
          const ud = panelsGrp.userData;
          at.panel = [ud.cxs[ud.cxs.length - 1] + 0.25, ud.top + 0.1, ud.rz + 0.35];
          at.mount = [ud.rxs[0], ud.ym - 0.58, ud.rz + 0.22];
        } else if (dach) {
          const ud = panelsGrp.userData;
          const pc = lp(ud.refs[ud.refs.length - 1]);
          at.panel = [pc[0] + U.x * 0.5 + N.x * 0.45, pc[1] + U.y * 0.5 + N.y * 0.45, pc[2] + U.z * 0.5 + N.z * 0.45];
          at.mount = [ud.cxs[0] - 0.42, ROOF.top + 0.3, ud.z0 + 0.15];
        } else {
          at.panel = [panelsGrp.userData.refs[panelsGrp.userData.refs.length - 1].position.x, panelsGrp.userData.top + 0.34, 0.9];
          at.socket = [-0.55, 1.16, -0.44];
          if (batGrp) {
            at.battery = [batGrp.position.x, batGrp.userData.top + 0.2, batGrp.position.z + 0.26];
            at.inverter = [batGrp.position.x - 0.34, batGrp.userData.top - 0.12, batGrp.position.z + 0.3];
          } else {
            at.inverter = [invGrp.position.x, invGrp.userData.top + 0.14, WALL_Z + 0.14];
          }
        }
        hotspots.forEach((hs) => {
          const on = !!at[hs.key];
          hs.off = !on;
          hs.el.style.display = on ? '' : 'none';
          if (on) hs.anchor.position.set(at[hs.key][0], at[hs.key][1], at[hs.key][2]);
          if (hs.key === 'inverter') hs.tip.textContent = s.battery ? INFO.inverterInt : INFO.inverterBox;
          else if (hs.key === 'panel') hs.tip.textContent = trapez ? INFO.panelTrapez : zaun ? INFO.panelZaun : dach ? INFO.panelDach : INFO.panel;
          else if (hs.key === 'mount') {
            hs.tip.textContent = trapez ? INFO.mountTrapez : zaun ? INFO.mountZaun : INFO.mount;
            hs.el.querySelector('b').textContent = trapez ? 'Trapezdachmontage' : zaun ? 'Zaunmontage' : hs.label;
          } else hs.tip.textContent = INFO[hs.key];
        });
      }

      /* ------------------------------------------------- Kamera & Einpassung */
      const orbit = makeOrbit(THREE, camera, renderer.domElement, new THREE.Vector3());
      this._orbit = orbit;
      this._wantsRotate = !this.hasAttribute('no-autorotate') &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      orbit.state.autoRotate = this._wantsRotate;

      const gutters = () => {
        const w = Math.max(1, this._wrap.clientWidth);
        const h = Math.max(1, this._wrap.clientHeight);
        const card = this._card;
        return {
          w, h,
          left: Math.min(w * 0.45, card.offsetLeft + card.offsetWidth + 26),
          bottom: 30
        };
      };

      let framed = false, lastMount = null;
      function frame() {
        const g = gutters();
        const dach = self._cfg.mount === 'dach', zaun = self._cfg.mount === 'zaun', trapez = self._cfg.mount === 'trapez';
        const b = new THREE.Box3().setFromObject(root);
        if (b.isEmpty()) return;
        // Modell auf dem Boden absetzen, ohne den Ursprung zu verschieben
        root.position.y -= b.min.y;
        root.updateMatrixWorld(true);
        b.setFromObject(root);
        ground.position.y = b.min.y;

        const sph = b.getBoundingSphere(new THREE.Sphere());
        const span = sph.radius * 1.08;
        Object.assign(key.shadow.camera, { left: -span, right: span, top: span, bottom: -span, near: 0.5, far: 30 });
        key.shadow.camera.updateProjectionMatrix();
        key.target.position.copy(sph.center);
        key.target.updateMatrixWorld();

        // Fokus: Balkon → ganze Szene, Flachdach → Modulfeld auf dem Carport
        const f = trapez ? new THREE.Box3().setFromObject(panelsGrp).expandByVector(new THREE.Vector3(0.5, 0.3, 0.5))
          : zaun ? new THREE.Box3().setFromObject(panelsGrp).expandByVector(new THREE.Vector3(0.35, 0.3, 0.35))
          : dach ? new THREE.Box3().setFromObject(panelsGrp).expandByVector(new THREE.Vector3(0.3, 0.2, 0.3)) : b;
        const c = f.getCenter(new THREE.Vector3());
        const size = f.getSize(new THREE.Vector3());

        // Der Sichtbereich wird nach links/unten verschoben, damit die Anlage
        // rechts der Karte sitzt — gedreht wird trotzdem um die Fokusmitte.
        camera.aspect = g.w / g.h;
        camera.setViewOffset(g.w, g.h, -g.left / 2, g.bottom / 2, g.w, g.h);

        const sweptW = Math.hypot(size.x, size.z);
        const usableW = Math.max(220, g.w - g.left - 30);
        const usableH = Math.max(220, g.h - g.bottom - 44);
        const vFov = (camera.fov * Math.PI) / 180;
        const hFov = 2 * Math.atan(Math.tan(vFov / 2) * (g.w / g.h));
        const halfV = Math.atan(Math.tan(vFov / 2) * (usableH / g.h));
        const halfH = Math.atan(Math.tan(hFov / 2) * (usableW / g.w));
        const dist = Math.max((size.y / 2) / Math.tan(halfV), (sweptW / 2) / Math.tan(halfH)) * (dach ? 1.45 : trapez ? 1.4 : zaun ? 1.15 : 1.06) + 0.25;
        camera.updateProjectionMatrix();

        const pref = (dach || trapez ? new THREE.Vector3(1, 1.0, 1.3) : zaun ? new THREE.Vector3(0.9, 0.42, 1.5) : new THREE.Vector3(1, 0.5, 1.25)).normalize();
        if (!framed) {
          orbit.target.copy(c);
          camera.position.copy(c).add(pref.clone().multiplyScalar(dist));
          orbit.sync(); orbit.apply();
          framed = true;
        } else {
          orbit.glide(c, dist, lastMount !== self._cfg.mount ? Math.acos(pref.y) : null);
        }
        lastMount = self._cfg.mount;
      }
      this._rebuild = rebuild;

      const resize = () => {
        const w = this._wrap.clientWidth, h = this._wrap.clientHeight;
        if (!w || !h) return;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        frame();
      };
      this._ro = new ResizeObserver(resize);
      this._ro.observe(this._wrap);

      rebuild();
      resize();

      /* ------------------------------------------------------------- Schleife */
      const v3 = new THREE.Vector3();
      const placeHotspots = () => {
        const w = this._wrap.clientWidth, h = this._wrap.clientHeight;
        const cl = this._card.offsetLeft, ct = this._card.offsetTop;
        const cr = cl + this._card.offsetWidth, cb = ct + this._card.offsetHeight;
        const live = [];
        for (const hs of hotspots) {
          if (hs.off) continue;
          hs.anchor.getWorldPosition(v3).project(camera);
          const x = (v3.x * 0.5 + 0.5) * w;
          const y = (-v3.y * 0.5 + 0.5) * h;
          const behind = v3.z > 1;
          hs.el.style.visibility = behind ? 'hidden' : '';
          if (behind) continue;
          hs.el.style.left = x + 'px';
          hs.el.style.top = y + 'px';
          const overCard = x + 20 > cl && x - 20 < cr && y + 20 > ct && y - 20 < cb;
          hs.el.style.opacity = overCard ? '0' : '1';
          hs.el.style.pointerEvents = overCard ? 'none' : 'auto';
          hs.el.classList.toggle('fl', x > w - 170);
          hs.el.classList.toggle('fr', x < Math.max(170, cr + 140));
          hs.el.classList.toggle('down', y < 230);
          hs.sx = x; hs.sy = y;
          live.push(hs);
        }
        for (let i = 0; i < live.length; i++) {
          for (let j = i + 1; j < live.length; j++) {
            const a = live[i], b = live[j];
            const dx = b.sx - a.sx, dy = b.sy - a.sy, d = Math.hypot(dx, dy);
            if (d < 42) {
              const push = (42 - d) / 2 + 2;
              const ux = d < 0.001 ? 1 : dx / d, uy = d < 0.001 ? 0 : dy / d;
              a.sx -= ux * push; a.sy -= uy * push; b.sx += ux * push; b.sy += uy * push;
              a.el.style.left = a.sx + 'px'; a.el.style.top = a.sy + 'px';
              b.el.style.left = b.sx + 'px'; b.el.style.top = b.sy + 'px';
            }
          }
        }
      };

      let last = performance.now();
      let visible = true;
      const vis = new IntersectionObserver((es) => { visible = es.some((e) => e.isIntersecting); }, { rootMargin: '120px' });
      vis.observe(this);
      this._vis = vis;

      const loop = (t) => {
        if (this._dead) return;
        this._raf = requestAnimationFrame(loop);
        const dt = Math.min(0.05, (t - last) / 1000);
        last = t;
        if (!visible) return;
        orbit.update(dt);
        renderer.render(scene, camera);
        placeHotspots();
      };
      this._raf = requestAnimationFrame(loop);
    }
  }

  customElements.define('bkw-konfigurator', BkwKonfigurator);
})();
